import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import {
  Container,
  Box,
  Typography,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  Stack,
  Chip,
  InputLabel,
  Button,
  Checkbox,
  ListItemText,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip as MuiTooltip,
  Divider,
  Paper,
  List,
  ListItemButton,
  TextField,
  Collapse,
  Tabs,
  Tab,
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsIcon from '@mui/icons-material/Settings';
import EditIcon from '@mui/icons-material/Edit';
import { Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import ChartCard from '../components/ChartCard.js';
import VarLabelToggle from '../components/VarLabelToggle.js';
import { useVarLabelFormatter } from '../hooks/useVarLabelFormatter.js';
import { fetchPivotOptions } from '../utils/fetchPivotOptions.js';
import TableCard, { TableCardPanel } from '../components/TableCard.js';
import AnalysisCard from '../components/AnalysisCard.js';
import { BASE_COLOR_THEMES, DEFAULT_THEME_ID, getThemeById, mergeThemeLists, normalizePalette, sanitizeHexColor } from '../constants/colorThemes.js';


import { useAuth } from '../contexts/AuthContext';

import { db } from '../firebase';
import {
  doc, getDoc, setDoc, addDoc, getDocs,
  serverTimestamp, collection, query, where, limit, orderBy, deleteDoc
} from 'firebase/firestore';

// Normalize option payloads coming from Firestore/SPSS metadata
const normalizeColumnOptions = (options = []) => {
  const seen = new Set();
  const normalized = [];

  options.forEach((opt, idx) => {
    if (opt == null) return;

    let value;
    let label;

    if (typeof opt === 'object') {
      const rawValue = opt.value ?? opt.code ?? opt.id ?? opt.key ?? opt.option ?? opt.name;
      const rawLabel = opt.label ?? opt.text ?? opt.name ?? opt.title ?? opt.display;

      if (rawValue != null && String(rawValue).trim() !== '') {
        value = String(rawValue).trim();
      }

      if (rawLabel != null && String(rawLabel).trim() !== '') {
        label = String(rawLabel).trim();
      }

      // Fall back to label as value when only label is provided
      if (!value && label) {
        value = label;
      }

      // If still missing, fall back to first truthy primitive on the object
      if (!value) {
        const firstPrimitive = Object.values(opt).find((v) => typeof v !== 'object' && v != null && String(v).trim() !== '');
        if (firstPrimitive != null) {
          value = String(firstPrimitive).trim();
        }
      }
    } else {
      value = String(opt).trim();
    }

    if (!value) return;

    // Skip common sentinel values
    if (value === '0') return;

    if (!label) {
      label = value;
    }

    if (seen.has(value)) return;
    seen.add(value);
    normalized.push({ value, label });
  });

  return normalized;
};

const collectFallbackOptionsForColumn = (col, fallbackIndex) => {
  if (!col || !fallbackIndex) return [];

  const aggregated = [];
  const seenKeys = new Set();

  const registerByKey = (raw) => {
    if (raw == null) return;
    const str = String(raw).trim();
    if (!str || seenKeys.has(str)) return;
    seenKeys.add(str);

    const lower = str.toLowerCase();
    const opts =
      (fallbackIndex.exact && fallbackIndex.exact.get(str)) ||
      (fallbackIndex.lower && fallbackIndex.lower.get(lower)) ||
      [];

    if (Array.isArray(opts) && opts.length) {
      aggregated.push(...opts);
    }
  };

  registerByKey(col?.id);
  registerByKey(col?.header);

  const listKeys = [
    col?.aliases,
    col?.columns,
    col?.columnHeaders,
    col?.columnIds,
    col?.columnIDs,
    col?.sourceHeaders,
  ];

  listKeys.forEach((list) => {
    if (!Array.isArray(list)) return;
    list.forEach(registerByKey);
  });

  return aggregated;
};

const normalizeFilterColumns = (columns = [], colTypes = [], fallbackIndex = null) => {
  const byId = new Map();

  const nestedKeys = ['columns', 'members', 'items', 'children', 'subquestions', 'scaleItems'];

  const collectOptions = (col) => {
    if (!col) return [];

    const candidates = [];
    const candidateKeys = [
      'options',
      'optionLabels',
      'scale',
      'likertScale',
      'groupLikertScale',
      'answers',
      'answerOptions',
      'values',
      'valueOptions',
    ];

    const visit = (node) => {
      if (!node || typeof node !== 'object') return;

      candidateKeys.forEach((key) => {
        const val = node[key];
        if (Array.isArray(val) && val.length) {
          candidates.push(val);
        }
      });

      if (node.valueLabels && typeof node.valueLabels === 'object') {
        const entries = Object.entries(node.valueLabels).map(([value, label]) => ({ value, label }));
        if (entries.length) candidates.push(entries);
      }

      nestedKeys.forEach((key) => {
        const nest = node[key];
        if (Array.isArray(nest) && nest.length) {
          nest.forEach((child) => visit(child));
        }
      });
    };

    visit(col);

    const flattened = [];
    candidates.forEach((arr) => {
      if (!Array.isArray(arr)) return;
      arr.forEach((opt) => flattened.push(opt));
    });

    return normalizeColumnOptions(flattened);
  };

  const allColumns = [
    ...(Array.isArray(columns) ? columns : []),
    ...(Array.isArray(colTypes) ? colTypes : []),
  ];

  allColumns.forEach((col) => {
    if (!col) return;

    const idRaw = col.id ?? col.header;
    if (idRaw == null) return;
    const id = String(idRaw).trim();
    if (!id) return;

    const headerRaw = col.header ?? col.id;
    const header = headerRaw != null ? String(headerRaw).trim() : id;

    const baseOptions = collectOptions(col);
    const fallbackOptions = collectFallbackOptionsForColumn(col, fallbackIndex);
    const normalizedOptions = normalizeColumnOptions([...baseOptions, ...fallbackOptions]);

    if (!byId.has(id)) {
      byId.set(id, {
        ...col,
        id,
        header,
        options: normalizedOptions,
      });
      return;
    }

    const existing = byId.get(id);
    const mergedOptions = normalizeColumnOptions([
      ...(existing.options || []),
      ...normalizedOptions,
    ]);

    byId.set(id, {
      ...existing,
      ...col,
      id,
      header,
      options: mergedOptions,
    });
  });

  return Array.from(byId.values()).sort((a, b) =>
    String(a.header || '').localeCompare(String(b.header || ''), 'tr', { sensitivity: 'base' })
  );
};

const toTrimmedString = (val) => {
  if (val == null) return '';
  if (typeof val === 'string') return val.trim();
  return String(val).trim();
};

const extractAnswerOptionsFromGraph = (graph) => {
  if (!graph || typeof graph !== 'object') return [];

  const rawCandidates = [];

  const pushCandidate = (value, label) => {
    if (value == null) return;
    const val = toTrimmedString(value);
    if (!val) return;
    const lbl = label != null ? toTrimmedString(label) : val;
    rawCandidates.push({ value: val, label: lbl || val });
  };

  const collectFromArray = (arr) => {
    if (!Array.isArray(arr)) return;
    arr.forEach((item) => {
      if (item == null) return;
      if (typeof item === 'object') {
        const value =
          item.value ??
          item.code ??
          item.id ??
          item.key ??
          item.option ??
          item.optionValue ??
          item.answerValue ??
          item.answer ??
          item.label ??
          item.name ??
          item.text;
        const label =
          item.label ??
          item.text ??
          item.name ??
          item.display ??
          item.answerLabel ??
          item.answer ??
          item.title ??
          item.optionLabel ??
          item.optionText ??
          item.valueLabel;
        if (value != null || label != null) {
          pushCandidate(value != null ? value : label, label != null ? label : value);
        }
        return;
      }
      pushCandidate(item, item);
    });
  };

  collectFromArray(graph.answerOptions);
  collectFromArray(graph.answers);
  collectFromArray(graph.options);
  collectFromArray(graph.labels);
  collectFromArray(graph.seriesLabels);

  const data = graph.data || {};
  collectFromArray(data.answerOptions);
  collectFromArray(data.answers);
  collectFromArray(data.labels);
  collectFromArray(data.options);

  const records = data.records;
  if (Array.isArray(records)) {
    records.forEach((rec) => {
      if (!rec || typeof rec !== 'object') return;
      collectFromArray(rec.options);
      collectFromArray(rec.answers);
      const value =
        rec.value ??
        rec.valueRaw ??
        rec.code ??
        rec.answerValue ??
        rec.option ??
        rec.id ??
        rec.key ??
        rec.header ??
        rec.label ??
        rec.answer;
      const label =
        rec.label ??
        rec.answer ??
        rec.text ??
        rec.display ??
        rec.name ??
        rec.optionLabel ??
        rec.optionText ??
        rec.valueLabel;
      if (value != null || label != null) {
        pushCandidate(value != null ? value : label, label != null ? label : value);
      }
    });
  }

  return normalizeColumnOptions(rawCandidates);
};

const collectGraphIdentifiers = (graph) => {
  const keys = new Set();
  if (!graph || typeof graph !== 'object') return keys;

  const add = (val) => {
    const str = toTrimmedString(val);
    if (str) keys.add(str);
  };

  add(graph.id);
  add(graph.header);
  add(graph.questionId);
  add(graph.questionID);
  add(graph.question_id);
  add(graph.columnId);
  add(graph.columnID);
  add(graph.column_id);
  add(graph.column);
  add(graph.variable);
  add(graph.varname);

  const meta = graph.meta || {};
  add(meta.id);
  add(meta.header);
  add(meta.name);
  add(meta.questionId);
  add(meta.questionID);
  add(meta.question_id);
  add(meta.baseQuestionId);
  add(meta.variable);
  add(meta.column);
  add(meta.columnId);
  add(meta.columnID);
  add(meta.column_id);
  add(meta.baseHeader);
  add(meta.baseColumn);

  const data = graph.data || {};
  add(data.questionId);
  add(data.questionID);
  add(data.question_id);
  add(data.columnId);
  add(data.column);
  add(data.header);

  const arrayCandidates = [
    graph.cols,
    graph.columns,
    graph.columnHeaders,
    graph.columnIds,
    graph.columnIDs,
    graph.headers,
    graph.questionIds,
    graph.questionIDs,
    meta.columns,
    meta.columnHeaders,
    meta.columnIds,
    meta.columnIDs,
    meta.sourceHeaders,
    meta.headers,
    meta.questionIds,
    data.columns,
    data.columnHeaders,
    data.columnIds,
    data.columnIDs,
    data.headers,
    data.questionIds,
  ];

  arrayCandidates.forEach((candidate) => {
    if (!Array.isArray(candidate)) return;
    candidate.forEach(add);
  });

  return keys;
};

const extractGraphsFromPayload = (payload) => {
  if (!payload || typeof payload !== 'object') return [];
  const graphs = [];

  const pushList = (list) => {
    if (!Array.isArray(list)) return;
    list.forEach((item) => {
      if (item) graphs.push(item);
    });
  };

  pushList(payload.basegraphs);
  pushList(payload.graphs);
  if (Array.isArray(payload.groups)) {
    payload.groups.forEach((grp) => {
      pushList(grp?.graphs);
    });
  }

  return graphs;
};

const buildGraphAnswerOptionIndex = (graphs = [], payload = null) => {
  const exact = new Map();
  const lower = new Map();

  const register = (key, options) => {
    if (!key || !Array.isArray(options) || !options.length) return;
    const str = toTrimmedString(key);
    if (!str) return;
    const normalized = normalizeColumnOptions(options);
    if (!normalized.length) return;
    const prev = exact.get(str) || [];
    const merged = normalizeColumnOptions([...prev, ...normalized]);
    exact.set(str, merged);
    lower.set(str.toLowerCase(), merged);
  };

  const processGraph = (graph) => {
    if (!graph) return;
    const options = extractAnswerOptionsFromGraph(graph);
    if (!options.length) return;
    const keys = collectGraphIdentifiers(graph);
    keys.forEach((key) => register(key, options));
  };

  (Array.isArray(graphs) ? graphs : []).forEach(processGraph);
  extractGraphsFromPayload(payload).forEach(processGraph);

  return { exact, lower };
};

// ---- Debounce helper for Firestore writes (reduces write bursts) ----
function createDebouncer() {
  const timers = new Map();
  return function debounce(key, fn, delay = 600) {
    if (timers.has(key)) clearTimeout(timers.get(key));
    const t = setTimeout(() => {
      timers.delete(key);
      try { fn(); } catch (e) { console.warn('Debounced write failed', e); }
    }, delay);
    timers.set(key, t);
  };
}
const debounceFS = createDebouncer();

// ---- Merge helper to preserve SPSS labels on API refresh ----
function mergeColumnTypes(prevCols = [], nextCols = []) {
  try {
    const byKey = (arr) => {
      const m = new Map();
      (arr || []).forEach((c) => {
        if (!c) return;
        const id = c.id || c.header;
        const header = c.header || c.id;
        const key = String(id || header || '');
        if (key) m.set(key, c);
      });
      return m;
    };

    const prevMap = byKey(prevCols);

    return (nextCols || []).map((nx) => {
      const id = nx?.id || nx?.header;
      const header = nx?.header || nx?.id;
      const key = String(id || header || '');
      const pv = prevMap.get(key) || {};

      // Preserve SPSS-related labels if missing in incoming payload
      const varLabel   = nx.varLabel   != null ? nx.varLabel   : pv.varLabel;
      const groupLabel = nx.groupLabel != null ? nx.groupLabel : pv.groupLabel;
      const groupname  = nx.groupname  != null ? nx.groupname  : pv.groupname;

      // Prefer incoming options but fall back to previous if missing
      const options = Array.isArray(nx.options) ? nx.options : pv.options;

      return { ...pv, ...nx, varLabel, groupLabel, groupname, options };
    });
  } catch (e) {
    console.warn('mergeColumnTypes failed:', e);
    return nextCols || [];
  }
}

const createGroupId = () => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch (_) {}
  return `grp_${Math.random().toString(36).slice(2, 12)}`;
};

const generateGroupName = (groups = []) => {
  const normalizedNames = new Set(
    (Array.isArray(groups) ? groups : [])
      .map((g) => (g?.name ? String(g.name).trim().toLowerCase() : ''))
      .filter(Boolean)
  );
  const base = 'Grup';
  let idx = 1;
  let candidate = `${base} ${idx}`;
  while (normalizedNames.has(candidate.toLowerCase())) {
    idx += 1;
    candidate = `${base} ${idx}`;
  }
  return candidate;
};

const sanitizeFilterRows = (rows = []) => {
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => {
    const col = row?.col != null ? String(row.col).trim() : '';
    const values = Array.isArray(row?.vals)
      ? Array.from(
          new Set(
            row.vals
              .map((val) => (val == null ? '' : String(val)))
              .filter((val) => val.trim() !== '')
          )
        )
      : [];
    return { col, vals: values };
  });
};

const sanitizeWeightRows = (rows = []) => {
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => {
    const cols = Array.isArray(row?.cols)
      ? Array.from(
          new Set(
            row.cols
              .map((val) => (val == null ? '' : String(val)))
              .filter((val) => val.trim() !== '')
          )
        )
      : [];
    const weightCol = row?.weightCol != null ? String(row.weightCol).trim() : '';
    return { cols, weightCol };
  });
};

const BASE_GRAPH_GROUP_ID = '__basegraphs__';

const countGraphsInPayload = (payload) => {
  if (!payload || typeof payload !== 'object') return 0;

  const baseCount = Array.isArray(payload.basegraphs) ? payload.basegraphs.length : 0;
  const groupedCount = Array.isArray(payload.groups)
    ? payload.groups.reduce(
        (sum, grp) => sum + (Array.isArray(grp?.graphs) ? grp.graphs.length : 0),
        0
      )
    : 0;

  if (baseCount + groupedCount > 0) {
    return baseCount + groupedCount;
  }

  return Array.isArray(payload.graphs) ? payload.graphs.length : 0;
};

const normalizeGraphPayload = (payload) => {
  const normalized = {
    graphs: [],
    groupMeta: {},
    groupOrder: [],
  };

  if (!payload || typeof payload !== 'object') {
    // Always expose the base group so UI can render an empty state gracefully
    normalized.groupMeta[BASE_GRAPH_GROUP_ID] = {
      id: BASE_GRAPH_GROUP_ID,
      name: 'Temel Grafikler',
      filtersApplied: [],
      weightsApplied: [],
      isBase: true,
      order: 0,
    };
    normalized.groupOrder.push(BASE_GRAPH_GROUP_ID);
    return normalized;
  }

  const toArray = (val) => (Array.isArray(val) ? val : []);
  const trimOr = (val, fallback) => {
    if (typeof val === 'string') {
      const trimmed = val.trim();
      if (trimmed) return trimmed;
    }
    return fallback;
  };

  // --- Base group ---
  const baseGraphs = toArray(payload.basegraphs?.length ? payload.basegraphs : payload.graphs);
  const baseMeta = {
    id: BASE_GRAPH_GROUP_ID,
    name: 'Temel Grafikler',
    filtersApplied: [],
    weightsApplied: [],
    isBase: true,
    order: 0,
  };
  normalized.groupMeta[BASE_GRAPH_GROUP_ID] = baseMeta;
  normalized.groupOrder.push(BASE_GRAPH_GROUP_ID);

  baseGraphs.forEach((graph, idx) => {
    if (!graph) return;
    normalized.graphs.push({
      ...graph,
      __groupId: BASE_GRAPH_GROUP_ID,
      __groupName: baseMeta.name,
      __groupIndex: idx,
      __groupFilters: baseMeta.filtersApplied,
      __groupWeights: baseMeta.weightsApplied,
      __isBaseGraph: true,
    });
  });

  // --- Additional groups ---
  toArray(payload.groups).forEach((grp, groupIdx) => {
    if (!grp) return;
    const id = trimOr(grp.id, `grp_${groupIdx + 1}`);
    const name = trimOr(grp.name, `Grup ${groupIdx + 1}`);
    const filtersApplied = toArray(grp.filtersApplied);
    const weightsApplied = toArray(grp.weightsApplied);

    const meta = {
      id,
      name,
      filtersApplied,
      weightsApplied,
      isBase: false,
      order: groupIdx + 1,
    };

    normalized.groupMeta[id] = meta;
    normalized.groupOrder.push(id);

    toArray(grp.graphs).forEach((graph, idx) => {
      if (!graph) return;
      normalized.graphs.push({
        ...graph,
        __groupId: id,
        __groupName: name,
        __groupIndex: idx,
        __groupFilters: filtersApplied,
        __groupWeights: weightsApplied,
        __isBaseGraph: false,
      });
    });
  });

  return normalized;
};

const areFilterRowsEqual = (a = [], b = []) => {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const rowA = a[i] || {};
    const rowB = b[i] || {};
    if ((rowA.col || '') !== (rowB.col || '')) return false;
    const valsA = Array.isArray(rowA.vals) ? rowA.vals : [];
    const valsB = Array.isArray(rowB.vals) ? rowB.vals : [];
    if (valsA.length !== valsB.length) return false;
    for (let j = 0; j < valsA.length; j += 1) {
      if (valsA[j] !== valsB[j]) return false;
    }
  }
  return true;
};

const areWeightRowsEqual = (a = [], b = []) => {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const rowA = a[i] || {};
    const rowB = b[i] || {};
    const colsA = Array.isArray(rowA.cols) ? rowA.cols : [];
    const colsB = Array.isArray(rowB.cols) ? rowB.cols : [];
    if (colsA.length !== colsB.length) return false;
    for (let j = 0; j < colsA.length; j += 1) {
      if (colsA[j] !== colsB[j]) return false;
    }
    if ((rowA.weightCol || '') !== (rowB.weightCol || '')) return false;
  }
  return true;
};

const sanitizeGroupsForState = (groups = []) => {
  if (!Array.isArray(groups) || !groups.length) return [];
  const usedIds = new Set();
  return groups.map((grp, idx) => {
    let id = grp?.id != null ? String(grp.id).trim() : '';
    if (!id || usedIds.has(id)) {
      id = createGroupId();
    }
    usedIds.add(id);

    const nameRaw = grp?.name != null ? String(grp.name).trim() : '';
    const name = nameRaw || `Grup ${idx + 1}`;

    return {
      id,
      name,
      filters: sanitizeFilterRows(grp?.filters),
      weights: sanitizeWeightRows(grp?.weights),
    };
  });
};

const buildGroupPersistPayload = (groups = [], requestedActiveId) => {
  const sanitized = sanitizeGroupsForState(groups);
  const normalized = sanitized.map((grp, idx) => ({
    id: grp.id || createGroupId(),
    name: grp.name && grp.name.trim() ? grp.name.trim() : `Grup ${idx + 1}`,
    filters: sanitizeFilterRows(grp.filters),
    weights: sanitizeWeightRows(grp.weights),
  }));

  const hasActive = typeof requestedActiveId === 'string' && normalized.some((g) => g.id === requestedActiveId);
  const activeId = hasActive ? requestedActiveId : (normalized[0]?.id || '');
  const activeGroup = normalized.find((g) => g.id === activeId) || normalized[0] || { filters: [], weights: [] };

  return {
    groups: normalized,
    activeId,
    filters: Array.isArray(activeGroup?.filters) ? activeGroup.filters : [],
    weights: Array.isArray(activeGroup?.weights) ? activeGroup.weights : [],
  };
};

const deriveGroupsFromSource = (source = {}, existing = []) => {
  if (!source || typeof source !== 'object') return [];

  if (Array.isArray(source.savedFilterGroups) && source.savedFilterGroups.length) {
    return sanitizeGroupsForState(source.savedFilterGroups);
  }

  const fallbackFilters = sanitizeFilterRows(source.savedFilters);
  const fallbackWeights = sanitizeWeightRows(source.savedWeights);
  if (!fallbackFilters.length && !fallbackWeights.length) return [];

  const name = generateGroupName(existing);
  return [
    {
      id: createGroupId(),
      name,
      filters: fallbackFilters,
      weights: fallbackWeights,
    },
  ];
};


// ---------------- Firestore Snapshot Helpers ----------------
const DASHBOARD_COLL = 'reporting-dashboards';

async function saveProjectSnapshot(projectID, analysisID, payload) {
  try {
    if (!projectID) return;
    const key = analysisID ? `${projectID}__${analysisID}` : String(projectID);
    const ref = doc(collection(db, DASHBOARD_COLL), key);

    // --- Keep snapshot tiny: only metadata, never full graphs/columns ---
    const meta = {
      generatedAt: payload?.generatedAt || new Date().toISOString(),
      graphCount : countGraphsInPayload(payload),
      colCount   : Array.isArray(payload?.columnTypes) ? payload.columnTypes.length : 0,
    };

    await setDoc(ref, { data: meta, updatedAt: serverTimestamp() }, { merge: true });
  } catch (e) {
    console.warn('Snapshot save failed:', e);
  }
}


async function loadProjectSnapshot(projectID, analysisID) {
  try {
    if (!projectID) return null;
    const key = analysisID ? `${projectID}__${analysisID}` : String(projectID);
    const ref = doc(collection(db, DASHBOARD_COLL), key);
    const snap = await getDoc(ref);
    return snap.exists() ? snap.data()?.data || null : null;
  } catch (e) {
    console.warn('Snapshot load failed:', e);
    return null;
  }
}

// Helper to load the latest FULL dataset from graphDatasets
async function loadLatestFullGraphDataset(organisation, projectID, analysisID) {
  try {
    if (!organisation || !projectID) return null;
    const basePath = analysisID
      ? ['organisations', organisation, 'analyses', analysisID]
      : ['organisations', organisation, 'projects', projectID];
    const collRef = collection(db, ...basePath, 'graphDatasets');

    // Try order by createdAt first
    let qs = await getDocs(query(collRef, orderBy('createdAt', 'desc'), limit(1)));
    if (qs.empty) {
      // Fallback: some older docs may have `createDate` instead
      qs = await getDocs(query(collRef, orderBy('createDate', 'desc'), limit(1)));
    }
    if (qs.empty) return null;

    const docSnap = qs.docs[0];
    const data = docSnap.data() || null;
    const graphCount = countGraphsInPayload(data);
    // Sanity: must contain at least one graph
    if (!data || graphCount === 0) return null;
    return data;
  } catch (e) {
    console.warn('loadLatestFullGraphDataset failed:', e);
    return null;
  }
}

const DEFAULT_SETTINGS = {
  legend: false,
  dataLabels: true,
  gridLines: true,
  autoScale: true, // false = start Y‑axis at zero
  visibleAnswers: null, // null → tüm cevaplar açık
  baseType: 'participants',          // 'none' | 'participants' | 'answers'
  scaleBoxes: [],
  sigIncludeOverall: true,
  generalTest: true,
  visible: true,
};

const normalizeThemeId = (id) => {
  if (typeof id !== 'string') return null;
  const trimmed = id.trim();
  return trimmed || null;
};

const DEFAULT_NEW_THEME_COLORS = (BASE_COLOR_THEMES[0]?.palette || ['#8639A1', '#3F8EFC', '#82CA9D', '#FFC658']).slice(0, 6);

const buttonTheme = createTheme({
  palette: {
    primary: { main: '#5D276F' },
  },
  components: {
    MuiButton:      { defaultProps: { color: 'primary' } },
    MuiIconButton:  { defaultProps: { color: 'primary' } },
    MuiToggleButton:{ defaultProps: { color: 'primary' } },
  },
});

/* --------------------------------------------------------------- */
/*  Sayfa Bileşeni                                                 */
/* --------------------------------------------------------------- */
export default function GraphsDashboards() {
  const navigate = useNavigate();
  const { state } = useLocation();

  const { data, projectID, analysisID, columnTypes: navCols = [] } = state || {};
  // --- Save-loop guards ---
  const lastMetaSnapshotRef = useRef('');            // prevents repeated lightweight snapshot saves
  const lastFullPersistKeyRef = useRef('');          // prevents repeated FULL-payload persists
  const saveFilterGroups_lastJsonRef = useRef('');
  const didInitialFullPersistRef = useRef(false);
  const lastLoadedGroupSourceRef = useRef(null);
  const autoRefreshAttemptedRef = useRef(false);

  // --- Organisation must be defined BEFORE we reference it in hooks ---
  const [organisation, setOrganisation] = useState('');
  // Başlıkta göstermek için: DB'den gelen kod/isimler
  const [projectCode, setProjectCode] = useState('');
  const [analysisTitle, setAnalysisTitle] = useState('');
  

  // Analiz varsa tüm saved* kalıcı durum buraya yazılacak/okunacak
  const getAnalysisDocRef = useCallback((orgParam) => {
    const org = orgParam || organisation;
    if (!analysisID || !org) return null;
    return doc(db, 'organisations', org, 'analyses', analysisID);
  }, [analysisID, organisation]);

  const [loadedSnapshot, setLoadedSnapshot] = useState(null);
  const pageData = useMemo(() => data || loadedSnapshot || null, [data, loadedSnapshot]);

  // ---- Global SPSS labels toggle (single source of truth) ----
  const [showVarLabels, setShowVarLabels] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [analysisDefaultThemeId, setAnalysisDefaultThemeId] = useState(DEFAULT_THEME_ID);
  const [customColorThemes, setCustomColorThemes] = useState([]);
  const allColorThemes = useMemo(
    () => mergeThemeLists(customColorThemes),
    [customColorThemes]
  );
  const [themeEditor, setThemeEditor] = useState(() => ({
    open: false,
    mode: 'create',
    themeId: '',
    label: '',
    palette: DEFAULT_NEW_THEME_COLORS.slice(0),
    saving: false,
    deleteBusy: false,
    error: '',
  }));
  const themeEditorRef = useRef(null);
  const getInitialThemePalette = useCallback(
    () => (DEFAULT_NEW_THEME_COLORS.length ? DEFAULT_NEW_THEME_COLORS : ['#8639A1', '#3F8EFC', '#82CA9D']).slice(0, 6),
    []
  );
  useEffect(() => {
    themeEditorRef.current = themeEditor;
  }, [themeEditor]);
  const colorThemesCollectionRef = useMemo(
    () => (organisation ? collection(db, 'organisations', organisation, 'colorThemes') : null),
    [organisation]
  );
  const canEditSelectedTheme = useMemo(
    () => customColorThemes.length > 0,
    [customColorThemes]
  );
  const persistDefaultThemeSelection = useCallback((themeId) => {
    if (!organisation) return;

    let targetDoc = null;
    if (analysisID) {
      targetDoc = doc(db, 'organisations', organisation, 'analyses', analysisID);
    } else if (projectID) {
      targetDoc = doc(db, 'organisations', organisation, 'projects', projectID);
    }
    if (!targetDoc) return;

    const normalized = normalizeThemeId(themeId) || DEFAULT_THEME_ID;
    const doWrite = async () => {
      await setDoc(
        targetDoc,
        { analysisDefaultThemeId: normalized },
        { merge: true }
      );
    };
    debounceFS('analysisDefaultTheme', doWrite, 500);
  }, [analysisID, organisation, projectID]);
  const handleChangeAnalysisDefaultTheme = useCallback((themeId) => {
    const normalized = normalizeThemeId(themeId) || DEFAULT_THEME_ID;
    setAnalysisDefaultThemeId((prev) => {
      if (prev === normalized) return prev;
      persistDefaultThemeSelection(normalized);
      return normalized;
    });
  }, [persistDefaultThemeSelection]);
  const openCreateThemeDialog = useCallback(() => {
    setThemeEditor({
      open: true,
      mode: 'create',
      themeId: '',
      label: '',
      palette: getInitialThemePalette(),
      saving: false,
      deleteBusy: false,
      error: '',
    });
  }, [getInitialThemePalette]);
  const openEditThemeDialog = useCallback(() => {
    const target =
      customColorThemes.find((theme) => theme.id === analysisDefaultThemeId) ||
      customColorThemes[0];
    if (!target) return;
    setThemeEditor({
      open: true,
      mode: 'edit',
      themeId: target.id,
      label: target.label,
      palette: Array.isArray(target.palette) && target.palette.length
        ? [...target.palette]
        : getInitialThemePalette(),
      saving: false,
      deleteBusy: false,
      error: '',
    });
  }, [analysisDefaultThemeId, customColorThemes, getInitialThemePalette]);
  const closeThemeEditor = useCallback(() => {
    setThemeEditor((prev) => ({
      ...prev,
      open: false,
      saving: false,
      deleteBusy: false,
      error: '',
    }));
  }, []);
  const handleThemeLabelChange = useCallback((value) => {
    setThemeEditor((prev) => ({ ...prev, label: value }));
  }, []);
  const handleThemePaletteChange = useCallback((index, value) => {
    setThemeEditor((prev) => {
      const palette = Array.isArray(prev.palette) ? [...prev.palette] : [];
      const sanitized = sanitizeHexColor(value) || value;
      palette[index] = sanitized;
      return { ...prev, palette };
    });
  }, []);
  const handleThemePaletteAdd = useCallback(() => {
    setThemeEditor((prev) => {
      const palette = Array.isArray(prev.palette) ? [...prev.palette] : [];
      if (palette.length >= 10) return prev;
      const nextColor =
        palette.length > 0
          ? palette[palette.length - 1]
          : getInitialThemePalette()[0] || '#000000';
      palette.push(nextColor);
      return { ...prev, palette };
    });
  }, [getInitialThemePalette]);
  const handleThemePaletteRemove = useCallback((index) => {
    setThemeEditor((prev) => {
      const palette = Array.isArray(prev.palette) ? [...prev.palette] : [];
      if (palette.length <= 2) return prev;
      palette.splice(index, 1);
      return { ...prev, palette };
    });
  }, []);
  const handleThemeSave = useCallback(async () => {
    const current = themeEditorRef.current;
    if (!colorThemesCollectionRef || !current) {
      setThemeEditor((prev) => ({ ...prev, error: 'Tema kaydedilemedi: organizasyon bulunamadı.' }));
      return;
    }
    const label = typeof current.label === 'string' ? current.label.trim() : '';
    const palette = normalizePalette(current.palette);
    if (!label) {
      setThemeEditor((prev) => ({ ...prev, error: 'Tema adı gerekli.' }));
      return;
    }
    if (palette.length < 2) {
      setThemeEditor((prev) => ({ ...prev, error: 'En az iki renk seçmelisiniz.' }));
      return;
    }
    setThemeEditor((prev) => ({ ...prev, saving: true, error: '' }));
    try {
      if (current.mode === 'edit' && current.themeId) {
        const ref = doc(colorThemesCollectionRef, current.themeId);
        await setDoc(
          ref,
          {
            label,
            palette,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
        setCustomColorThemes((prev) =>
          prev.map((theme) =>
            theme.id === current.themeId ? { ...theme, label, palette } : theme
          )
        );
      } else {
        const docRef = await addDoc(colorThemesCollectionRef, {
          label,
          palette,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        setCustomColorThemes((prev) => [...prev, { id: docRef.id, label, palette }]);
      }
      setThemeEditor((prev) => ({
        ...prev,
        open: false,
        saving: false,
        deleteBusy: false,
        error: '',
      }));
    } catch (e) {
      console.warn('saveCustomTheme failed:', e);
      setThemeEditor((prev) => ({
        ...prev,
        saving: false,
        error: 'Tema kaydedilirken hata oluştu. Lütfen tekrar deneyin.',
      }));
    }
  }, [colorThemesCollectionRef]);
  const handleThemeDelete = useCallback(async () => {
    const current = themeEditorRef.current;
    if (!colorThemesCollectionRef || !current?.themeId) return;
    setThemeEditor((prev) => ({ ...prev, deleteBusy: true, error: '' }));
    try {
      const ref = doc(colorThemesCollectionRef, current.themeId);
      await deleteDoc(ref);
      setCustomColorThemes((prev) => prev.filter((theme) => theme.id !== current.themeId));
      if (analysisDefaultThemeId === current.themeId) {
        const fallback = BASE_COLOR_THEMES[0]?.id || DEFAULT_THEME_ID;
        handleChangeAnalysisDefaultTheme(fallback);
      }
      setThemeEditor((prev) => ({
        ...prev,
        open: false,
        saving: false,
        deleteBusy: false,
        error: '',
      }));
    } catch (e) {
      console.warn('deleteCustomTheme failed:', e);
      setThemeEditor((prev) => ({
        ...prev,
        deleteBusy: false,
        error: 'Tema silinemedi. Lütfen tekrar deneyin.',
      }));
    }
  }, [analysisDefaultThemeId, colorThemesCollectionRef, handleChangeAnalysisDefaultTheme]);

  const [filterGroups, setFilterGroups] = useState([]);
  const [activeFilterGroupId, setActiveFilterGroupId] = useState('');

  const activeFilterGroup = useMemo(() => {
    if (!Array.isArray(filterGroups) || !filterGroups.length) return null;
    if (activeFilterGroupId) {
      const match = filterGroups.find((grp) => grp && grp.id === activeFilterGroupId);
      if (match) return match;
    }
    return filterGroups[0] || null;
  }, [filterGroups, activeFilterGroupId]);

  const filterRows = useMemo(
    () => (Array.isArray(activeFilterGroup?.filters) ? activeFilterGroup.filters : []),
    [activeFilterGroup]
  );

  const weightRows = useMemo(
    () => (Array.isArray(activeFilterGroup?.weights) ? activeFilterGroup.weights : []),
    [activeFilterGroup]
  );

  const filterGroupPayload = useMemo(
    () => buildGroupPersistPayload(filterGroups, activeFilterGroupId),
    [filterGroups, activeFilterGroupId]
  );

  const persistFilterGroupsState = useCallback(
    (groups, activeId) => {
      const payload = buildGroupPersistPayload(groups, activeId);

      // Keep fallback source in sync with latest local intent so refresh doesn't resurrect removed groups
      lastLoadedGroupSourceRef.current = {
        ...(lastLoadedGroupSourceRef.current || {}),
        savedFilterGroups: payload.groups,
        savedFilters: payload.filters,
        savedWeights: payload.weights,
      };

      const json = JSON.stringify(payload);
      if (saveFilterGroups_lastJsonRef.current === json) {
        return;
      }
      saveFilterGroups_lastJsonRef.current = json;

      if (!organisation || !analysisID) {
        return;
      }

      const doWrite = async () => {
        await setDoc(
          doc(db, 'organisations', organisation, 'analyses', analysisID),
          {
            savedFilterGroups: payload.groups,
            activeFilterGroupId: payload.activeId || null,
            savedFilters: payload.filters,
            savedWeights: payload.weights,
          },
          { merge: true }
        );
      };
      debounceFS('filterGroups', doWrite, 700);
    },
    [organisation, analysisID]
  );

  const ensureGroupList = useCallback(
    (groups) => {
      // Yeni istek: başlangıçta hiçbir grup oluşturma.
      // Yalnızca kullanıcı "Grup Oluştur" dediğinde ilk grup oluşsun.
      return Array.isArray(groups) ? groups : [];
    },
    []
  );

  const updateActiveGroupFilters = useCallback(
    (nextFilters) => {
      const sanitizedFilters = sanitizeFilterRows(nextFilters);
      if (areFilterRowsEqual(sanitizedFilters, filterRows)) return;
      // Grup yoksa otomatik oluşturma yapma (kullanıcıdan "Grup Oluştur" beklenir)
      if (!Array.isArray(filterGroups) || filterGroups.length === 0) {
        return;
      }
      let computedGroups = null;
      let computedActiveId = activeFilterGroupId;
      setFilterGroups((prev) => {
        const base = Array.isArray(prev) ? [...prev] : [];
        if (base.length === 0) return prev; // grup yoksa dokunma
        let activeId = activeFilterGroupId && base.some((g) => g.id === activeFilterGroupId)
          ? activeFilterGroupId
          : base[0].id;
        const idx = base.findIndex((g) => g.id === activeId);
        const updatedGroup = { ...base[idx], filters: sanitizedFilters };
        const nextGroups = [...base];
        nextGroups[idx] = updatedGroup;
        computedGroups = nextGroups;
        computedActiveId = activeId;
        return nextGroups;
      });
      if (computedGroups) {
        if (computedActiveId !== activeFilterGroupId) {
          setActiveFilterGroupId(computedActiveId);
        }
        persistFilterGroupsState(computedGroups, computedActiveId);
      }
    },
    [activeFilterGroupId, filterGroups, persistFilterGroupsState, filterRows]
  );

  const updateActiveGroupWeights = useCallback(
    (nextWeights) => {
      const sanitizedWeights = sanitizeWeightRows(nextWeights);
      if (areWeightRowsEqual(sanitizedWeights, weightRows)) return;
      if (!Array.isArray(filterGroups) || filterGroups.length === 0) {
        return;
      }
      let computedGroups = null;
      let computedActiveId = activeFilterGroupId;
      setFilterGroups((prev) => {
        const base = Array.isArray(prev) ? [...prev] : [];
        if (base.length === 0) return prev;
        let activeId = activeFilterGroupId && base.some((g) => g.id === activeFilterGroupId)
          ? activeFilterGroupId
          : base[0].id;
        const idx = base.findIndex((g) => g.id === activeId);
        const updatedGroup = { ...base[idx], weights: sanitizedWeights };
        const nextGroups = [...base];
        nextGroups[idx] = updatedGroup;
        computedGroups = nextGroups;
        computedActiveId = activeId;
        return nextGroups;
      });
      if (computedGroups) {
        if (computedActiveId !== activeFilterGroupId) {
          setActiveFilterGroupId(computedActiveId);
        }
        persistFilterGroupsState(computedGroups, computedActiveId);
      }
    },
    [activeFilterGroupId, filterGroups, persistFilterGroupsState, weightRows]
  );

  const updateActiveGroupName = useCallback(
    (nextName) => {
      const targetName = typeof nextName === 'string' ? nextName : '';
      let computedGroups = null;
      const trimmed = targetName;
      if ((activeFilterGroup?.name ?? '') === trimmed) return;
      if (!Array.isArray(filterGroups) || filterGroups.length === 0) {
        return;
      }
      setFilterGroups((prev) => {
        const base = Array.isArray(prev) ? [...prev] : [];
        if (base.length === 0) return prev;
        let activeId = activeFilterGroupId && base.some((g) => g.id === activeFilterGroupId)
          ? activeFilterGroupId
          : base[0].id;
        const idx = base.findIndex((g) => g.id === activeId);
        const updatedGroup = { ...base[idx], name: trimmed };
        const nextGroups = [...base];
        nextGroups[idx] = updatedGroup;
        computedGroups = nextGroups;
        return nextGroups;
      });
      if (computedGroups) {
        const resolvedActive = activeFilterGroupId && computedGroups.some((g) => g.id === activeFilterGroupId)
          ? activeFilterGroupId
          : computedGroups[0].id;
        if (resolvedActive !== activeFilterGroupId) {
          setActiveFilterGroupId(resolvedActive);
        }
        persistFilterGroupsState(computedGroups, resolvedActive);
      }
    },
    [activeFilterGroupId, activeFilterGroup, filterGroups, persistFilterGroupsState]
  );

  const handleCreateFilterGroup = useCallback(() => {
    const base = Array.isArray(filterGroups) ? [...filterGroups] : [];
    const name = generateGroupName(base);
    const newGroup = {
      id: createGroupId(),
      name,
      filters: [],
      weights: [],
    };
    const nextGroups = [...base, newGroup];
    setFilterGroups(nextGroups);
    setActiveFilterGroupId(newGroup.id);
    persistFilterGroupsState(nextGroups, newGroup.id);
    setNeedsRefresh(true);
  }, [filterGroups, persistFilterGroupsState]);

  const handleSelectFilterGroup = useCallback(
    (id) => {
      if (!id || id === activeFilterGroupId) return;
      if (!Array.isArray(filterGroups) || !filterGroups.some((g) => g.id === id)) return;
      setActiveFilterGroupId(id);
      persistFilterGroupsState(filterGroups, id);
      setNeedsRefresh(true);
    },
    [filterGroups, activeFilterGroupId, persistFilterGroupsState]
  );

  const handleDeleteActiveFilterGroup = useCallback(() => {
    if (!Array.isArray(filterGroups) || !filterGroups.length) return;
    const currentId = activeFilterGroupId || (filterGroups[0]?.id || '');
    const activeName = activeFilterGroup?.name || 'Seçili Grup';
    const canConfirm = typeof window !== 'undefined' && window.confirm;
    if (canConfirm && !window.confirm(`${activeName} grubunu silmek istediğinize emin misiniz?`)) {
      return;
    }
    const remaining = filterGroups.filter((grp) => grp && grp.id !== currentId);
    const nextGroups = remaining; // Boş diziye izin ver
    const nextActiveId = nextGroups[0]?.id || '';
    setFilterGroups(nextGroups);
    setActiveFilterGroupId(nextActiveId);
    persistFilterGroupsState(nextGroups, nextActiveId);
    setNeedsRefresh(true);
  }, [filterGroups, activeFilterGroupId, activeFilterGroup, persistFilterGroupsState]);


  const [colTypes, setColTypes] = useState(navCols);
  const [pivotOpts, setPivotOpts] = useState({
    colTypes: [],
    rowGroupOptions: [],
    leafColumnOptions: [],
    displayCols: [],
    mainQuestionsPayload: null,
  });
  // Select'lerde ve grafiklerde SPSS varLabel göstermek için,
  // öncelik: Firestore'dan gelen colTypes → yoksa window fallback
  const metaSrc = useMemo(
    () =>
      (Array.isArray(colTypes) && colTypes.length
        ? colTypes
        : (window.__columnTypes || window.__projectRawData || [])),
    [colTypes]
  );
  const fmt = useVarLabelFormatter(metaSrc, showVarLabels);

  useEffect(() => {
    if (Array.isArray(navCols) && navCols.length) return; // keep initial
    const colsFromSnapshot = pageData?.columnTypes || pageData?.colTypes || [];
    if (colsFromSnapshot.length) setColTypes(colsFromSnapshot);
  }, [pageData, navCols]);

  // 1) Persist latest API result (pivot / crosstab / multiqustion) when it arrives via navigation state
  useEffect(() => {
    if (!projectID) return;
    const payload = data || null;
    const hasGraphs = payload && (
      countGraphsInPayload(payload) > 0 ||
      payload.pivots ||
      payload.crosstabs ||
      payload.multiquestion ||
      payload.multiqustion
    );
    if (!hasGraphs) return;

    // Build a stable key to detect duplicates for this session
    const key = [
      projectID,
      analysisID || '',
      payload.generatedAt || 'no-gen',
      countGraphsInPayload(payload),
      Array.isArray(payload.columnTypes) ? payload.columnTypes.length : 0
    ].join('::');

    if (lastMetaSnapshotRef.current === key) return; // already saved this payload meta
    lastMetaSnapshotRef.current = key;

    saveProjectSnapshot(projectID, analysisID, payload);
  }, [projectID, analysisID, data]);

  

  // 2) On first mount, if page opened without data, try to load last snapshot (prefer FULL dataset)
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!projectID) return;
      if (data) return; // we already have fresh data via navigation
      if (!organisation) return; // need org to resolve graphDatasets path

      // First, try FULL dataset from graphDatasets
      const full = await loadLatestFullGraphDataset(organisation, projectID, analysisID);
      if (mounted && full) {
        setLoadedSnapshot(full);
        navigate('.', { replace: true, state: { ...(state || {}), data: full, columnTypes: full.columnTypes, __origin: 'snapshot' } });
        return;
      }

      // Fallback to lightweight snapshot (metadata-only) for backward compatibility
      const snap = await loadProjectSnapshot(projectID, analysisID);
      if (mounted && snap) {
        setLoadedSnapshot(snap);
        navigate('.', { replace: true, state: { ...(state || {}), data: snap, __origin: 'snapshot' } });
      }
    })();
    return () => { mounted = false; };
  }, [organisation, projectID, analysisID, data]);
  

  useEffect(() => {
    try {
      if (organisation) localStorage.setItem('organisation', organisation);
      if (projectID)    localStorage.setItem('projectID', String(projectID));
      if (analysisID)   localStorage.setItem('analysisID', String(analysisID));
    } catch {}
  }, [organisation, projectID, analysisID]);
  
  // Map header -> id  ve  id -> header (eski kayıtlara uyum)
  const idByHeader = useMemo(() => {
    const m = {};
    (colTypes || []).forEach((c) => {
      if (!c) return;
      const id = c.id || c.header;
      const header = c.header || c.id;
      if (header) m[header] = id;
    });
    return m;
  }, [colTypes]);

  const headerById = useMemo(() => {
    const m = {};
    (colTypes || []).forEach((c) => {
      if (!c) return;
      const id = c.id || c.header;
      const header = c.header || c.id;
      if (id) m[id] = header;
    });
    return m;
  }, [colTypes]);

  // Ana dönüştürücüler
  const toId = useCallback((key) => {
    if (!key) return '';
    return idByHeader[key] || key;  // zaten id ise koru
  }, [idByHeader]);

  const toHeader = useCallback((key) => {
    if (!key) return '';
    return headerById[key] || key;
  }, [headerById]);

  const toIds = useCallback((arr) => (Array.isArray(arr) ? arr.map(toId) : []), [toId]);

  const { currentUser } = useAuth();

  const [graphGroupMeta, setGraphGroupMeta] = useState(() => ({
    [BASE_GRAPH_GROUP_ID]: {
      id: BASE_GRAPH_GROUP_ID,
      name: 'Temel Grafikler',
      filtersApplied: [],
      weightsApplied: [],
      isBase: true,
      order: 0,
    },
  }));
  const [graphGroupOrder, setGraphGroupOrder] = useState([BASE_GRAPH_GROUP_ID]);
  const [graphs, setGraphs] = useState([]);
  // --- NEW: Table Cards state ---
  const [tableCards, setTableCards] = useState([]); // [{id, title, columns, rows}]
  const [tableBuilderInputs, setTableBuilderInputs] = useState(null);
  const [activeTab, setActiveTab] = useState('graphs'); // 'graphs' | 'tables' | 'analysis'
  const [graphSettings, setGraphSettings] = useState({}); // { header: settings }
  const graphSettingsRef = useRef(graphSettings);
  // --- Search state for graphs list ---
  const [graphSearch, setGraphSearch] = useState('');

  const prepareGraphForPresentation = useCallback(
    (graph) => {
      if (!graph) return null;
      const enriched = { ...graph };

      const normalizeStr = (val) => {
        if (val == null) return '';
        const str = String(val).trim();
        return str;
      };

      const resolvedGroupId =
        graph.__groupId ??
        graph.groupId ??
        graph.group_id ??
        graph.meta?.groupId ??
        graph.meta?.group_id ??
        graph.group?.id ??
        '';

      const resolvedGroupName =
        graph.__groupName ??
        graph.groupName ??
        graph.group_name ??
        graph.meta?.groupName ??
        graph.meta?.group_name ??
        graph.group?.name ??
        '';

      if (!enriched.__groupId && resolvedGroupId) {
        enriched.__groupId = normalizeStr(resolvedGroupId);
      }
      if (!enriched.__groupName && resolvedGroupName) {
        enriched.__groupName = normalizeStr(resolvedGroupName);
      }

      if (!enriched.__analysisId && analysisID) {
        enriched.__analysisId = normalizeStr(analysisID);
      }

      if (!enriched.__analysisName) {
        const analysisLabel = normalizeStr(analysisTitle) || normalizeStr(analysisID);
        if (analysisLabel) {
          enriched.__analysisName = analysisLabel;
        }
      }

      if (!enriched.__projectId && projectID) {
        enriched.__projectId = normalizeStr(projectID);
      }
      if (!enriched.__projectCode && projectCode) {
        enriched.__projectCode = normalizeStr(projectCode);
      }

      return enriched;
    },
    [analysisID, analysisTitle, projectCode, projectID]
  );

  const handleBuilderInputsChange = useCallback((payload) => {
    setTableBuilderInputs(payload);
  }, []);

useEffect(() => {
  graphSettingsRef.current = graphSettings;
}, [graphSettings]);

useEffect(() => {
  if (!analysisDefaultThemeId) return;
  const exists = allColorThemes.some((theme) => theme.id === analysisDefaultThemeId);
  if (!exists) {
    const fallback = BASE_COLOR_THEMES[0]?.id || DEFAULT_THEME_ID;
      setAnalysisDefaultThemeId(fallback);
    }
  }, [analysisDefaultThemeId, allColorThemes]);

  useEffect(() => {
    if (!colorThemesCollectionRef) {
      setCustomColorThemes([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(colorThemesCollectionRef);
        if (cancelled) return;
        const themes = (snap.docs || [])
          .map((docSnap) => {
            const data = docSnap.data() || {};
            const id = docSnap.id;
            const label = typeof data.label === 'string' ? data.label.trim() : '';
            const palette = normalizePalette(data.palette);
            if (!id || !label || palette.length === 0) return null;
            return { id, label, palette };
          })
          .filter(Boolean);
        setCustomColorThemes(themes);
      } catch (e) {
        console.warn('loadCustomColorThemes failed:', e);
        setCustomColorThemes([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [colorThemesCollectionRef]);

  // Her grafiği benzersiz tanımlamak için anahtar (header aynı olsa da farklı grafikleri ayırır)
  const graphKeyOf = useCallback((g) => {
    try {
      const type = g?.graphType || 'unknown';
      const head = g?.header || '';
      const sig = JSON.stringify({
        meta: g?.meta || '',
        series: g?.data?.series || [],
        pivot: g?.pivot || g?.data?.pivot || null,
        cols: g?.cols || g?.data?.labels || [],
      });
      return `${type}::${head}::${sig}`;
    } catch {
      return `${g?.graphType || 'unknown'}::${g?.header || ''}`;
    }
  }, []);

  // Gizli grafikler (settings.visible === false olanlar)
  const hiddenGraphs = useMemo(
    () =>
      (graphs || []).filter(
        (g) =>
          g?.settings?.visible === false ||
          graphSettings[graphKeyOf(g)]?.visible === false ||
          graphSettings[g.header]?.visible === false
      ),
    [graphs, graphSettings]
  );

  const hiddenHeaders = useMemo(
    () =>
      Array.from(new Set(hiddenGraphs.map((g) => String(g.header || ''))))
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b, 'tr')),
    [hiddenGraphs]
  );


  const mergeSettings = useCallback(
    (graphsArr, settingsMap) =>
      graphsArr.map((g) => {
        const k   = graphKeyOf(g);
        const raw = settingsMap[k] ?? settingsMap[g.header] ?? {};
        const s   = { ...DEFAULT_SETTINGS, ...raw };
        return { ...g, settings: s };
      }),
    [],
  );

  useEffect(() => {
    const normalized = normalizeGraphPayload(pageData);
    setGraphGroupMeta(normalized.groupMeta);
    setGraphGroupOrder(
      normalized.groupOrder.length ? normalized.groupOrder : [BASE_GRAPH_GROUP_ID]
    );
    setGraphs(mergeSettings(normalized.graphs, graphSettingsRef.current || {}));
  }, [pageData, mergeSettings]);

  // Helper: Normalize "likert" or "likert_scale" graphs for rendering as bar charts
  const normalizeGraphForRender = useCallback((g) => {
    try {
      if (!g) return g;
      // Treat new likert_scale graphs as bar charts
      if (g.graphType === 'likert' || g.questionType === 'likert_scale') {
        const labels = g?.data?.labels || [];
        const counts = g?.data?.counts || [];
        const series = [{ name: 'Likert Dağılımı', data: counts }];
        return {
          ...g,
          graphType: 'bar',
          data: {
            ...(g.data || {}),
            labels,
            series,
          },
        };
      }
      return g;
    } catch {
      return g;
    }
  }, []);

  const WEIGHT_SELECT_ALL_VALUE = '__weight_select_all__';

  // pivot yardımcı fonksiyonlarının altına
  const addWeightRow = () => {
    const next = [...weightRows, { cols: [], weightCol: '' }];
    updateActiveGroupWeights(next);
    setNeedsRefresh(true);
  };
  const handleWeightChange = (idx, vals) => {
    const incomingValues = Array.isArray(vals) ? vals : [];
    const allHeaders = weightColumnHeaders;
    const prevRow = weightRows[idx] ?? { cols: [] };
    let nextCols;

    if (incomingValues.includes(WEIGHT_SELECT_ALL_VALUE)) {
      const hasAllSelected =
        allHeaders.length > 0 &&
        prevRow.cols.length === allHeaders.length &&
        allHeaders.every((header) => prevRow.cols.includes(header));
      nextCols = hasAllSelected ? [] : allHeaders;
    } else {
      const cleaned = incomingValues
        .filter((val) => val !== WEIGHT_SELECT_ALL_VALUE)
        .map((val) => String(val))
        .filter((val) => val.trim() !== '');
      const normalized = allHeaders.length
        ? cleaned.filter((val) => allHeaders.includes(val))
        : cleaned;
      nextCols = Array.from(new Set(normalized));
    }

    const next = weightRows.map((r, i) => (i === idx ? { ...r, cols: nextCols } : r));
    updateActiveGroupWeights(next);
    setNeedsRefresh(true);
  };
  const handleWeightCol = (idx, val) => {
    const value = val == null ? '' : String(val);
    const next = weightRows.map((r, i) => (i === idx ? { ...r, weightCol: value } : r));
    updateActiveGroupWeights(next);
    setNeedsRefresh(true);
  };
  const removeWeightRow = (idx) => {
    const next = weightRows.filter((_, i) => i !== idx);
    updateActiveGroupWeights(next);
    setNeedsRefresh(true);
  };


  const [presentations, setPresentations] = useState([]);
  const [addDlg,       setAddDlg]       = useState({open:false, graph:null, presId:''});
  const [deleteDlg, setDeleteDlg] = useState({ open: false, header: '', busy: false });

  useEffect(()=>{
    if(!organisation) return;
    (async ()=>{
      const snap = await getDocs(collection(db,'organisations',organisation,'presentations'));
      setPresentations(snap.docs.map(d=>({id:d.id, ...d.data()})));
    })();
  },[organisation]);

  const [isLoading, setIsLoading] = useState(false);
  const [needsRefresh, setNeedsRefresh] = useState(false);

  const [selectedGraphKeys, setSelectedGraphKeys] = useState([]);
  const toggleSelectedGraph = useCallback((graphKey) => {
    if (!graphKey) return;
    setSelectedGraphKeys((prev) =>
      prev.includes(graphKey) ? prev.filter((k) => k !== graphKey) : [...prev, graphKey]
    );
  }, []);

  const [activeGraphGroupId, setActiveGraphGroupId] = useState(BASE_GRAPH_GROUP_ID);
  const [openGraphGroupId, setOpenGraphGroupId] = useState(BASE_GRAPH_GROUP_ID);

  const graphByKey = useMemo(() => {
    const map = new Map();
    (graphs || []).forEach((g) => {
      const key = graphKeyOf(g);
      map.set(key, g);
    });
    return map;
  }, [graphs, graphKeyOf]);

  useEffect(() => {
    setSelectedGraphKeys((prev) => {
      const filtered = prev.filter((key) => graphByKey.has(key));
      return filtered.length === prev.length ? prev : filtered;
    });
  }, [graphByKey]);

  const hiddenGraphKeySet = useMemo(() => {
    const set = new Set();
    (hiddenGraphs || []).forEach((g) => {
      set.add(graphKeyOf(g));
    });
    return set;
  }, [hiddenGraphs, graphKeyOf]);

  const orderedGraphGroups = useMemo(() => {
    const list = [];
    const seen = new Set();
    const fallbackMeta = (id) => ({
      id,
      name: id === BASE_GRAPH_GROUP_ID ? 'Temel Grafikler' : id,
      filtersApplied: [],
      weightsApplied: [],
      isBase: id === BASE_GRAPH_GROUP_ID,
      order: list.length,
    });

    (graphGroupOrder || []).forEach((id) => {
      seen.add(id);
      const meta = graphGroupMeta?.[id] || fallbackMeta(id);
      list.push({ id, meta });
    });

    Object.keys(graphGroupMeta || {}).forEach((id) => {
      if (seen.has(id)) return;
      const meta = graphGroupMeta[id] || fallbackMeta(id);
      list.push({ id, meta });
    });

    if (list.length === 0) {
      list.push({ id: BASE_GRAPH_GROUP_ID, meta: fallbackMeta(BASE_GRAPH_GROUP_ID) });
    }

    return list;
  }, [graphGroupOrder, graphGroupMeta]);

  const graphsByGroup = useMemo(() => {
    const groupMap = new Map();
    const ensureGroup = (id) => {
      if (!groupMap.has(id)) {
        groupMap.set(id, new Map());
      }
      return groupMap.get(id);
    };

    (graphs || []).forEach((g) => {
      if (!g) return;
      const key = graphKeyOf(g);
      if (hiddenGraphKeySet.has(key)) return;
      const groupId = g.__groupId || BASE_GRAPH_GROUP_ID;
      const header = String(g.header || '').trim();
      if (!header) return;
      const headerMap = ensureGroup(groupId);
      if (!headerMap.has(header)) {
        headerMap.set(header, { graph: g, key });
      }
    });

    const result = {};
    orderedGraphGroups.forEach(({ id }) => {
      const headerMap = groupMap.get(id);
      if (!headerMap) {
        result[id] = [];
        return;
      }
      const arr = Array.from(headerMap.values());
      arr.sort((a, b) =>
        fmt(a.graph.header).localeCompare(fmt(b.graph.header), 'tr', { sensitivity: 'base' })
      );
      result[id] = arr;
    });

    groupMap.forEach((headerMap, id) => {
      if (result[id]) return;
      const arr = Array.from(headerMap.values());
      arr.sort((a, b) =>
        fmt(a.graph.header).localeCompare(fmt(b.graph.header), 'tr', { sensitivity: 'base' })
      );
      result[id] = arr;
    });

    return result;
  }, [graphs, orderedGraphGroups, fmt, graphKeyOf, hiddenGraphKeySet]);

  const allGraphEntries = useMemo(() => {
    const entries = [];
    Object.values(graphsByGroup).forEach((items) => {
      (items || []).forEach((item) => entries.push(item));
    });
    return entries;
  }, [graphsByGroup]);

  const filteredGraphsByGroup = useMemo(() => {
    const term = graphSearch.trim().toLowerCase();
    if (!term) return graphsByGroup;
    const filtered = {};
    Object.entries(graphsByGroup).forEach(([id, items]) => {
      filtered[id] = items.filter(({ graph }) =>
        fmt(graph.header).toLowerCase().includes(term)
      );
    });
    return filtered;
  }, [graphsByGroup, graphSearch, fmt]);

  const allVisibleGraphKeys = useMemo(() => {
    const set = new Set();
    Object.values(filteredGraphsByGroup).forEach((items) => {
      items.forEach(({ key }) => set.add(key));
    });
    return Array.from(set);
  }, [filteredGraphsByGroup]);

  const defaultGraphList = useMemo(() => allGraphEntries.map((entry) => entry.graph), [allGraphEntries]);

  const displayedGraphs = useMemo(
    () => selectedGraphKeys.map((key) => graphByKey.get(key)).filter(Boolean),
    [selectedGraphKeys, graphByKey]
  );

  const hasGraphSearch = graphSearch.trim().length > 0;
  const hasVisibleGraphs = allVisibleGraphKeys.length > 0;
 
  useEffect(() => {
    const order = Array.isArray(graphGroupOrder) && graphGroupOrder.length
      ? graphGroupOrder
      : [BASE_GRAPH_GROUP_ID];
    const fallback = order[0];

    if (!order.includes(activeGraphGroupId)) {
      setActiveGraphGroupId(fallback);
    }

    if (openGraphGroupId && !order.includes(openGraphGroupId)) {
      setOpenGraphGroupId(fallback);
    }
  }, [graphGroupOrder, activeGraphGroupId, openGraphGroupId]);
  

  // Reset analysis‑scoped UI state when switching project/analysis to avoid carry‑over
  useEffect(() => {
    setFilterGroups([]);
    setActiveFilterGroupId('');
    saveFilterGroups_lastJsonRef.current = '';
    setGraphSettings({});
    setSelectedGraphKeys([]);
    setActiveGraphGroupId(BASE_GRAPH_GROUP_ID);
    setOpenGraphGroupId(BASE_GRAPH_GROUP_ID);
    autoRefreshAttemptedRef.current = false;
  }, [projectID, analysisID]);

  useEffect(() => {
    if (!currentUser?.uid) return;

    (async () => {
      // 1) kullanıcının organisation kodu
      const uSnap = await getDoc(doc(db, 'users', currentUser.uid));
      const org   = uSnap.data()?.organisation;
      setOrganisation(org);
      if (!org) return;

      // -- Proje dokümanı: sadece rawData (kolon/grup metadatası) için
      const projRef = doc(db, 'organisations', org, 'projects', projectID);
      const pSnap   = await getDoc(projRef);
      if (pSnap.exists()) {
        const d = pSnap.data();
        if (Array.isArray(d.rawData)) {
          setColTypes(d.rawData);
        }
        if (d && d.projectCode) {
          setProjectCode(d.projectCode);
        }
      }

      // -- Analiz dokümanı: saved* ve (varsa) graphSettings'in tek kaynağı
      let loadedGraphSettings = {};
      let defaultThemeCandidate = null;
      let groupSource = null;
      let activeGroupCandidate = '';
      try {
        const aRef = getAnalysisDocRef(org);
        if (aRef) {
          const aSnap = await getDoc(aRef);
          const aData = aSnap.exists() ? aSnap.data() : {};
          if (aData && aData.title) {
            setAnalysisTitle(aData.title);
          }
          loadedGraphSettings = aData.graphSettings || {};
          groupSource = aData;
          if (typeof aData.activeFilterGroupId === 'string') {
            activeGroupCandidate = aData.activeFilterGroupId;
          }
          const themeFromAnalysis =
            normalizeThemeId(aData?.analysisDefaultThemeId) ||
            normalizeThemeId(aData?.defaultColorThemeId) ||
            normalizeThemeId(aData?.defaultColorTheme);
          if (themeFromAnalysis) {
            defaultThemeCandidate = themeFromAnalysis;
          }
        } else {
          // Analiz yoksa proje seviyesindeki ayarları ve saved* alanlarını dene
          const pData = pSnap.exists() ? (pSnap.data() || {}) : {};
          loadedGraphSettings = pData.graphSettings || {};
          groupSource = pData;
          if (typeof pData.activeFilterGroupId === 'string') {
            activeGroupCandidate = pData.activeFilterGroupId;
          }
          const themeFromProject =
            normalizeThemeId(pData?.analysisDefaultThemeId) ||
            normalizeThemeId(pData?.defaultColorThemeId) ||
            normalizeThemeId(pData?.defaultColorTheme);
          if (themeFromProject) {
            defaultThemeCandidate = themeFromProject;
          }
        }
      } catch (e) {
        console.warn('Failed to load analysis/project settings', e);
      }

      // Keep a fallback copy of the loaded group source for later calls
      lastLoadedGroupSourceRef.current = groupSource || null;

      const derivedGroups = Array.isArray(groupSource?.savedFilterGroups) || Array.isArray(groupSource?.savedFilters)
        ? deriveGroupsFromSource(groupSource)
        : [];
      const ensuredGroups = Array.isArray(derivedGroups) ? [...derivedGroups] : [];
      const resolvedActiveId = ensuredGroups.length && ensuredGroups.some((grp) => grp.id === activeGroupCandidate)
        ? activeGroupCandidate
        : '';

      setFilterGroups(ensuredGroups);
      setActiveFilterGroupId(resolvedActiveId);
      const initialPayload = buildGroupPersistPayload(ensuredGroups, resolvedActiveId);
      saveFilterGroups_lastJsonRef.current = JSON.stringify(initialPayload);

      setGraphSettings(loadedGraphSettings);
      // var olan grafikleri anında bu ayarlarla birleştir
      setGraphs(prev => mergeSettings(prev, loadedGraphSettings));

      if (defaultThemeCandidate) {
        setAnalysisDefaultThemeId(defaultThemeCandidate);
      } else {
        setAnalysisDefaultThemeId((prev) => normalizeThemeId(prev) || DEFAULT_THEME_ID);
      }
      
    })();
  }, [currentUser?.uid, projectID, analysisID, mergeSettings, ensureGroupList]);

  useEffect(() => {
    (async () => {
      if (!organisation || !projectID) return;
      try {
        const opts = await fetchPivotOptions({
          db,
          organisation,
          projectID,
          analysisID
        });

        // Store fetched option sets for UI pickers
        setPivotOpts({
          colTypes: Array.isArray(opts.colTypes) ? opts.colTypes : [],
          rowGroupOptions: Array.isArray(opts.rowGroupOptions) ? opts.rowGroupOptions : [],
          leafColumnOptions: Array.isArray(opts.leafColumnOptions) ? opts.leafColumnOptions : [],
          displayCols: Array.isArray(opts.displayCols) ? opts.displayCols : [],
          mainQuestionsPayload: opts?.mainQuestionsPayload || null,
        });

        // Merge fetched columnTypes with existing ones to preserve labels
        if (Array.isArray(opts.colTypes) && opts.colTypes.length) {
          setColTypes(prev => mergeColumnTypes(prev, opts.colTypes));
        }
      } catch (e) {
        console.warn('fetchPivotOptions failed:', e);
      }
    })();
  }, [organisation, projectID, analysisID]);
  const handleSaveSettingsForGraph = async (graph, header, settings, applyKeys = []) => {
    const newMap = { ...graphSettings };
    const key = graphKeyOf(graph);

    // 1) Sadece bu grafiği güncelle
    const prev = newMap[key] || newMap[header] || DEFAULT_SETTINGS;
    const merged = { ...prev, ...settings };
    newMap[key] = merged;

    // 2) İsteğe bağlı yayılım: applyKeys dolu ise aynı sayfadaki diğer grafiklere SADECE bu anahtarları uygula
    if (Array.isArray(applyKeys) && applyKeys.length) {
      graphs.forEach((g2) => {
        if (g2 === graph) return;
        const k2 = graphKeyOf(g2);
        const p2 = newMap[k2] || newMap[g2.header] || DEFAULT_SETTINGS;
        const m2 = { ...p2 };
        applyKeys.forEach((ak) => {
          if (ak in settings) m2[ak] = settings[ak];
        });
        newMap[k2] = m2;
      });
    }

    // Avoid redundant writes if nothing changed
    const prevJson = JSON.stringify(graphSettings || {});
    const nextJson = JSON.stringify(newMap || {});

    setGraphSettings(newMap);
    setGraphs((prev) => mergeSettings(prev, newMap));

    if (prevJson === nextJson) return; // no-op if identical

    // Firestore'a yaz: analiz varsa analiz dokümanına, yoksa proje dokümanına
    if (organisation) {
      const targetDoc = analysisID
        ? doc(db, 'organisations', organisation, 'analyses', analysisID)
        : doc(db, 'organisations', organisation, 'projects', projectID);
      await setDoc(
        targetDoc,
        {
          graphSettings: newMap,
          savedWeights: weightRows,
          analysisDefaultThemeId: analysisDefaultThemeId,
        },
        { merge: true }
      );
    }
  };

  // Hafif kalıcı kayıt: filtre/weight/analiz ayarlarını analiz dokümanına kaydeder
  // Tüm değişiklikleri (grafik ayarları ve UI toggles dahil) analiz dokümanına kaydet
  const persistAllChanges = useCallback(async () => {
    if (!organisation || !analysisID) {
      console.warn('persistAllChanges skipped: analysisID yok');
      return;
    }
    const targetDoc = doc(db, 'organisations', organisation, 'analyses', analysisID);
    try {
      const payload = buildGroupPersistPayload(filterGroups, activeFilterGroupId);
      await setDoc(
        targetDoc,
        {
          savedFilterGroups: payload.groups,
          activeFilterGroupId: payload.activeId || null,
          savedFilters   : payload.filters,
          savedWeights   : payload.weights,
          graphSettings  : graphSettings,
          showVarLabels  : showVarLabels,
          analysisDefaultThemeId: analysisDefaultThemeId,
        },
        { merge: true }
      );
    } catch (e) {
      console.warn('Tüm değişiklikleri kaydederken hata', e);
    }
  }, [organisation, analysisID, filterGroups, activeFilterGroupId, graphSettings, showVarLabels, analysisDefaultThemeId]);

  

  /* Filtre nesnesini memoize */
  const buildFilters = useCallback(() => {
    const obj = {};
    filterRows.forEach((r) => {
      if (!r) return;
      const keyId = toId(r.col);
      const vals  = Array.isArray(r.vals) ? r.vals : [];
      if (keyId && vals.length) obj[keyId] = vals;
    });
    return obj;
  }, [filterRows, toId]);

  /* Backend çağrısı */
  const fetchGraphs = useCallback(
    async (extra = {}) => {
      setIsLoading(true);
      try {
        const formData = new FormData();

        const normalizeLikertScale = (rows) => {
          if (!Array.isArray(rows)) return [];
          return rows
            .map((row, idx) => {
              if (row == null) return null;

              if (typeof row === 'object' && !Array.isArray(row)) {
                const rawValue = row.value ?? row.score ?? row.weight;
                const numericValue = Number(rawValue);
                const fallbackValue = Number.isFinite(numericValue) ? Number(numericValue) : idx + 1;
                const code = row.code != null
                  ? String(row.code)
                  : row.value != null
                    ? String(row.value)
                    : '';
                const label = row.label != null
                  ? String(row.label)
                  : row.text != null
                    ? String(row.text)
                    : '';

                if (!code && !label) return null;
                return { code, label, value: fallbackValue };
              }

              const str = String(row);
              if (!str.trim()) return null;
              return { code: str, label: str, value: idx + 1 };
            })
            .filter(Boolean);
        };

        const deriveScaleFromValueLabels = (valueLabels) => {
          if (!valueLabels || typeof valueLabels !== 'object') return [];
          const entries = Object.entries(valueLabels).map(([code, label]) => ({
            code: String(code),
            label: label != null ? String(label) : String(code),
            value: Number(code),
          }));
          return normalizeLikertScale(entries);
        };

        const deriveScaleFromOptions = (options) => {
          if (!Array.isArray(options)) return [];
          const parsed = options.map((opt, idx) => {
            const str = String(opt ?? '').trim();
            if (!str) return null;
            const match = str.match(/^\s*(\d+(?:[.,]\d+)?)\s*[-–:]?\s*(.*)$/);
            const code = match ? match[1] : str;
            const label = match ? match[2] : str;
            return {
              code: String(code),
              label: label ? String(label).trim() : String(code),
              value: Number(code.replace(',', '.')),
              _fallbackIndex: idx,
            };
          }).filter(Boolean);

          return normalizeLikertScale(
            parsed.map((item) => ({
              code: item.code,
              label: item.label,
              value: Number.isFinite(item.value) ? Number(item.value) : item._fallbackIndex + 1,
            }))
          );
        };

        const buildLikertScale = (column, { preferGroup = false } = {}) => {
          if (!column) return [];

          const tryOrder = [];

          if (preferGroup) {
            tryOrder.push(column.groupLikertScale);
            tryOrder.push(column.likertScale);
          } else {
            tryOrder.push(column.likertScale);
            tryOrder.push(column.groupLikertScale);
          }

          for (const candidate of tryOrder) {
            const normalized = normalizeLikertScale(candidate);
            if (normalized.length) return normalized;
          }

          const fromValueLabels = deriveScaleFromValueLabels(column.valueLabels);
          if (fromValueLabels.length) return fromValueLabels;

          const fromOptions = deriveScaleFromOptions(column.options);
          if (fromOptions.length) return fromOptions;

          return [];
        };

        formData.append('projectID', projectID);
        formData.append('useremail', currentUser.email);
        // API artık Firestore'a yazıyor → gerekli meta/persist parametrelerini de gönder
        formData.append('organisation', organisation || '');
        formData.append('analysisID', analysisID || '');
        formData.append('activeFilterGroupId', activeFilterGroupId || '');

        // UI meta (filtre/weight) bilgilerini de gönder
        const _groupPayloadForApi = buildGroupPersistPayload(filterGroups, activeFilterGroupId);
        formData.append('filterRows', JSON.stringify(filterRows || []));
        formData.append('savedFilters', JSON.stringify(_groupPayloadForApi.filters || {}));
        formData.append('savedWeights', JSON.stringify(_groupPayloadForApi.weights || {}));

        /* Aktif olmayan grid kolonlarını da (griddata / multigriddata) API’ye gönder */
        const activeCols = colTypes.filter(
          c =>
            c && (c.active !== false || ['griddata', 'multigriddata'].includes(c.grouptype))
        );

        const likertGroupsMap = new Map();

        // Send a SLIM version to reduce payload size:
        const slimCols = activeCols.map((c) => {
          const base = {
            id        : c.id || c.header,
            header    : c.header || c.id,
            type      : c.type,
            grouptype : c.grouptype,
            groupname : c.groupname,
          };

          if (typeof c.groupLabel === 'string' && c.groupLabel.trim() !== '') {
            base.groupLabel = c.groupLabel.trim();
          }

          if (c.groupVisible !== undefined) {
            base.groupVisible = !!c.groupVisible;
          }

          const normalizedLikert = buildLikertScale(c);
          const columnLikertFlag = c.isLikert || c.groupLikert;
          if (columnLikertFlag) {
            base.isLikert = true;
            if (normalizedLikert.length) {
              base.likertScale = normalizedLikert;
            }
          }

          const normalizedGroupLikert = buildLikertScale(c, { preferGroup: true });
          if (c.groupLikert) {
            base.groupLikert = true;
            if (normalizedGroupLikert.length) {
              base.groupLikertScale = normalizedGroupLikert;
            } else if (normalizedLikert.length) {
              base.groupLikertScale = normalizedLikert;
            }
          }

          const hasGroupLikertFlag = c.groupLikert || (Array.isArray(c.groupLikertScale) && c.groupLikertScale.length);
          if (hasGroupLikertFlag && (c.groupname || c.groupLabel)) {
            const groupKey = c.groupname || (c.groupLabel ? `__label__${c.groupLabel}` : undefined);
            if (groupKey) {
              const existing = likertGroupsMap.get(groupKey) || {
                groupname: c.groupname || '',
                groupLabel: typeof c.groupLabel === 'string' ? c.groupLabel : '',
                columns: [],
                scale: normalizedGroupLikert.length ? normalizedGroupLikert : normalizedLikert,
              };
              existing.columns = Array.from(new Set([...(existing.columns || []), base.header]));
              if (!existing.scale?.length) {
                existing.scale = normalizedLikert;
              }
              if (!existing.groupLabel && c.groupLabel) {
                existing.groupLabel = c.groupLabel;
              }
              likertGroupsMap.set(groupKey, existing);
            }
          }

          // Only include options for multi-select types; trim very long arrays
          if (c.grouptype === 'multiplechoice' || c.type === 'multi-select data') {
            const opts = Array.isArray(c.options) ? c.options.slice(0, 100) : undefined;
            if (opts) base.options = opts;
          }

          return base;
        });

        const likertQuestions = activeCols
          .map((c) => {
            if (!c) return null;
            const header = c.header || c.id;
            if (!header) return null;

            const scale = buildLikertScale(c);
            if (!scale.length) return null;

            if (!(c.isLikert || c.groupLikert)) {
              // column has a usable scale (valueLabels/options) but not explicitly marked -> skip
              return null;
            }

            return {
              header,
              id: c.id || c.header || header,
              groupname: c.groupname || '',
              groupLabel: typeof c.groupLabel === 'string' ? c.groupLabel : '',
              scale,
            };
          })
          .filter(Boolean);

        const likertGroups = Array.from(likertGroupsMap.values())
          .filter((grp) => Array.isArray(grp.columns) && grp.columns.length && Array.isArray(grp.scale) && grp.scale.length);

        formData.append('columnTypes', JSON.stringify(slimCols));
        formData.append('likertQuestions', JSON.stringify(likertQuestions));
        formData.append('likertGroups', JSON.stringify(likertGroups));
        // ---- Robust group payload: prefer current state; fallback to last loaded saved groups ----
        let effectiveGroups = Array.isArray(filterGroups) && filterGroups.length
          ? filterGroups
          : deriveGroupsFromSource(lastLoadedGroupSourceRef.current, filterGroups);

        // Ensure array even if nothing found
        if (!Array.isArray(effectiveGroups)) effectiveGroups = [];

        const groupPayload = buildGroupPersistPayload(effectiveGroups, activeFilterGroupId);

        // Debug aid: keep a short console note if groups are empty while there is a known source
        if ((!groupPayload.groups || groupPayload.groups.length === 0) && lastLoadedGroupSourceRef.current) {
          console.warn('[fetchGraphs] filterGroups empty; fallback source existed but yielded no groups. Source keys:', Object.keys(lastLoadedGroupSourceRef.current || {}));
        }

        formData.append('filterGroups', JSON.stringify(groupPayload.groups || []));

        Object.entries(extra).forEach(([k, v]) => formData.append(k, v));


        const res = await fetch(
          'https://reporting-create-graph-795950092325.europe-west3.run.app',
          {
            method: 'POST',
            body: formData,
          }
        );
        const fresh = await res.json();

        console.log(fresh);
        const normalizedFresh = normalizeGraphPayload(fresh);
        setGraphGroupMeta(normalizedFresh.groupMeta);
        setGraphGroupOrder(
          normalizedFresh.groupOrder.length ? normalizedFresh.groupOrder : [BASE_GRAPH_GROUP_ID]
        );

        const mergedFresh = mergeSettings(
          normalizedFresh.graphs,
          graphSettingsRef.current || {}
        );
        setGraphs(mergedFresh);
        const mergedCols = mergeColumnTypes(colTypes, fresh.columnTypes || []);
        setColTypes(mergedCols);
        setNeedsRefresh(false);


      } catch (err) {
        console.error('Grafik yükleme hatası:', err);
      } finally {
        setIsLoading(false);
      }
    },
    [projectID, currentUser.email, colTypes, organisation, weightRows, mergeSettings, filterGroups, activeFilterGroupId, filterRows]
  );

  useEffect(() => {
    if (autoRefreshAttemptedRef.current) return;
    if (!projectID || !organisation || !currentUser?.email) return;
    if (isLoading) return;

    const initialGraphCount = countGraphsInPayload(pageData);
    const hasRenderedGraphs = Array.isArray(graphs) && graphs.length > 0;
    if (initialGraphCount > 0 || hasRenderedGraphs) return;

    autoRefreshAttemptedRef.current = true;
    fetchGraphs();
  }, [projectID, organisation, currentUser?.email, pageData, graphs, fetchGraphs, isLoading]);

  /* -------- UI Yardımcıları ------------- */
  const addRow = () => {
    const next = [...filterRows, { col: '', vals: [] }];
    updateActiveGroupFilters(next);
    setNeedsRefresh(true);
  };

  const handleColChange = (idx, col) => {
    const value = col == null ? '' : String(col);
    const next = filterRows.map((r, i) => (i === idx ? { col: value, vals: [] } : r));
    updateActiveGroupFilters(next);
    setNeedsRefresh(true);
  };

  const handleValChange = (idx, vals) => {
    const uniqueVals = Array.isArray(vals)
      ? Array.from(new Set(vals.map((v) => (v == null ? '' : String(v))).filter((v) => v !== '')))
      : [];
    const next = filterRows.map((r, i) => (i === idx ? { ...r, vals: uniqueVals } : r));
    updateActiveGroupFilters(next);
    setNeedsRefresh(true);
  };

  const removeRow = (idx) => {
    const next = filterRows.filter((_, i) => i !== idx);
    updateActiveGroupFilters(next);
    setNeedsRefresh(true);
  };


  const leafColumnOptions = pivotOpts.leafColumnOptions;

  const displayCols = pivotOpts.displayCols;
  const graphAnswerOptionIndex = useMemo(
    () => buildGraphAnswerOptionIndex(graphs, pageData),
    [graphs, pageData]
  );
  const weightColumnHeaders = useMemo(
    () =>
      (Array.isArray(displayCols) ? displayCols : [])
        .map((col) => {
          const header = col?.header ?? '';
          return typeof header === 'string' ? header.trim() : String(header ?? '').trim();
        })
        .filter((header) => header !== ''),
    [displayCols]
  );

  /* Filtrelenebilir kolonları normalize et (id + seçenek etiketleri) */
  const filterCols = useMemo(
    () => normalizeFilterColumns(displayCols, colTypes, graphAnswerOptionIndex),
    [displayCols, colTypes, graphAnswerOptionIndex]
  );

  // Eski kayıtlar header saklıyor olabilir; id'ye eşleştir
  useEffect(() => {
    if (!Array.isArray(filterRows) || !filterRows.length) return;
    if (!filterCols.length) return;

    const next = filterRows.map((row) => {
      if (!row || !row.col) return row;
      const match = filterCols.find((c) => c.id === row.col || c.header === row.col);
      if (!match || match.id === row.col) return row;
      return { ...row, col: match.id };
    });

    if (areFilterRowsEqual(next, filterRows)) return;

    updateActiveGroupFilters(next);
  }, [filterCols, filterRows, updateActiveGroupFilters]);


  // Bu analiz için grafiği gizle
  const hideGraph = useCallback((g) => {
    if (!g) return;
    handleSaveSettingsForGraph(g, g.header, { visible: false });
    const key = graphKeyOf(g);
    setSelectedGraphKeys((prev) => prev.filter((k) => k !== key));
  }, [handleSaveSettingsForGraph, graphKeyOf]);

  // Grafiği tamamen sil (yalnızca bu oturumdaki görünümden)
  // Grafiği tamamen sil (kalıcı) — hem UI'dan kaldır, hem de son graphDatasets dokümanından çıkar
  const handleDeleteChart = useCallback(async (header) => {
    if (!header) return;
    const key = String(header);

    const keysToRemove = new Set(
      (graphs || [])
        .filter((g) => String(g?.header) === key)
        .map((g) => graphKeyOf(g))
    );

    // 1) UI'dan hemen kaldır
    setGraphs((prev) => (Array.isArray(prev) ? prev.filter((g) => String(g?.header) !== key) : prev));
    setSelectedGraphKeys((prev) => prev.filter((graphKey) => !keysToRemove.has(graphKey)));
    setGraphSettings((prev) => {
      if (!prev || typeof prev !== 'object') return prev;
      const next = { ...prev };
      if (next[key]) delete next[key];
      return next;
    });

    // 2) Firestore üzerinde en güncel graphDatasets dokümanını bul ve güncelle
    try {
      if (!organisation || !projectID) return;
      const basePath = analysisID
        ? ['organisations', organisation, 'analyses', analysisID]
        : ['organisations', organisation, 'projects', projectID];
      const collRef = collection(db, ...basePath, 'graphDatasets');

      // createdAt -> yoksa createDate’e göre son doc
      let qs = await getDocs(query(collRef, orderBy('createdAt', 'desc'), limit(1)));
      if (qs.empty) {
        qs = await getDocs(query(collRef, orderBy('createDate', 'desc'), limit(1)));
      }
      if (qs.empty) return;

      const latest = qs.docs[0];
      const data = latest.data() || {};
      const normalized = normalizeGraphPayload(data);

      const filteredGraphs = normalized.graphs.filter((g) => String(g?.header) !== key);
      if (filteredGraphs.length === normalized.graphs.length) return; // zaten yok

      const stripMeta = (graph) => {
        if (!graph) return graph;
        const {
          __groupId,
          __groupName,
          __groupIndex,
          __groupFilters,
          __groupWeights,
          __isBaseGraph,
          ...rest
        } = graph;
        return rest;
      };

      // Split filtered graphs back into basegraphs + groups for persistence
      const nextBase = filteredGraphs
        .filter((g) => (g.__groupId || BASE_GRAPH_GROUP_ID) === BASE_GRAPH_GROUP_ID)
        .map(stripMeta);

      const groupedMap = new Map();
      filteredGraphs.forEach((g) => {
        const groupId = g.__groupId || BASE_GRAPH_GROUP_ID;
        if (groupId === BASE_GRAPH_GROUP_ID) return;
        if (!groupedMap.has(groupId)) {
          const meta = normalized.groupMeta?.[groupId] || { id: groupId, name: groupId, filtersApplied: [], weightsApplied: [] };
          groupedMap.set(groupId, { ...meta, graphs: [] });
        }
        const groupEntry = groupedMap.get(groupId);
        groupEntry.graphs.push(stripMeta(g));
      });

      const nextGroupsPayload = Array.from(groupedMap.values());
      const legacyGraphs = filteredGraphs.map(stripMeta);

      const prevDeleted = Array.isArray(data.deletedHeaders) ? data.deletedHeaders : [];
      const nextDeleted = Array.from(new Set([...prevDeleted, key]));

      await setDoc(latest.ref, {
        basegraphs: nextBase,
        groups: nextGroupsPayload,
        graphs: legacyGraphs,
        deletedHeaders: nextDeleted,
        summary: {
          ...(data.summary || {}),
          graphCount: filteredGraphs.length,
          lastDeletedHeader: key,
        },
        lastDeleteAt: new Date().toISOString(),
        updatedAt: serverTimestamp(),
      }, { merge: true });
    } catch (err) {
      console.warn('[handleDeleteChart] Firestore kalıcı silme başarısız:', err);
    }
  }, [organisation, projectID, analysisID, graphs, graphKeyOf]);

  // Başlığa bağlı gizlileri geri göster (tüm header-level ve per-graph visible'ı aç)
  const showHeaderGraphs = useCallback((hdr) => {
    if (!hdr) return;

    // Build updated settings: enable all graphs with this header
    const next = { ...graphSettings };

    // If there is a header-level flag that hides it, clear it
    if (next[hdr]?.visible === false) {
      delete next[hdr]; // header-level override no longer hides it
    }

    (graphs || []).forEach((gx) => {
      if (String(gx.header) === String(hdr)) {
        const k = graphKeyOf(gx);
        const prevS = next[k] || {};
        next[k] = { ...prevS, visible: true };
      }
    });

    setGraphSettings(next);
    setGraphs((prev) => mergeSettings(prev, next));

    const keysForHeader = (graphs || [])
      .filter((gx) => String(gx.header) === String(hdr))
      .map((gx) => graphKeyOf(gx));

    if (keysForHeader.length) {
      setSelectedGraphKeys((prev) => {
        const set = new Set(prev);
        keysForHeader.forEach((k) => set.add(k));
        return Array.from(set);
      });
    }
  }, [graphs, graphSettings, mergeSettings, graphKeyOf]);

  const surfaceBaseSx = {
    px: { xs: 2, md: 3 },
    py: { xs: 2, md: 2.5 },
  };

  const subtleChipSx = {
    borderColor: 'rgba(148, 163, 184, 0.4)',
    bgcolor: 'rgba(224, 231, 255, 0.6)',
    color: '#1f2937',
    fontWeight: 600,
    '& .MuiChip-icon': { color: '#3b82f6' },
  };

  const primaryButtonSx = {
    textTransform: 'none',
    fontWeight: 600,
    px: { xs: 2, md: 2.5 },
    py: 0.9,
    borderRadius: 999,
    boxShadow: '0 12px 24px rgba(148, 163, 184, 0.28)',
    border: '1px solid rgba(148, 163, 184, 0.25)',
    '&:hover': {
      boxShadow: '0 16px 28px rgba(148, 163, 184, 0.32)',
    },
  };

  const visibleGraphCount = allGraphEntries.length;
  const filterGroupCount = Array.isArray(filterGroups) ? filterGroups.length : 0;

  const heroChips = [
    projectCode || projectID ? `Veri Seti: ${projectCode || projectID}` : null,
    analysisID ? `Analiz: ${analysisTitle || analysisID}` : null,
    filterGroupCount
      ? `Aktif Grup: ${activeFilterGroup?.name?.trim() || 'Adsız Grup'}`
      : null,
  ].filter(Boolean);

  const heroSubtitle = analysisID
    ? `${analysisTitle || analysisID} analizi için oluşturulan grafik ve tabloları yönetin.`
    : 'Grafikleri düzenleyin, filtreleyin ve sunumlarınıza kolayca ekleyin.';

  if (countGraphsInPayload(pageData) === 0 && !(graphs?.length)) {
    return (
      <ThemeProvider theme={buttonTheme}>
        <Container sx={{ py: 4 }}>
          {isLoading ? (
            <Box sx={{ display:'flex', gap:2, alignItems:'center' }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Veriler yükleniyor…</Typography>
            </Box>
          ) : (
            <Typography variant="h6">Grafik verisi bulunamadı</Typography>
          )}
        </Container>
      </ThemeProvider>
    );
  }


  return (
    <ThemeProvider theme={buttonTheme}>
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(180deg, #f8fbff 0%, #eef2ff 50%, #fdf2f8 100%)',
          py: { xs: 3, md: 4.5 },
        }}
      >
        <Container
          maxWidth="xl"
          sx={{ px: { xs: 1.5, md: 3 } }}
        >
          {isLoading && (
            <Box
              sx={{
                position: 'fixed',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255,255,255,0.7)',
                zIndex: 9999,
              }}
            >
              <CircularProgress />
            </Box>
          )}

          <Stack spacing={2.25}>
            <Box
              sx={{
                borderRadius: 4,
                color: '#0f172a',
                border: '1px solid rgba(148, 163, 184, 0.2)',
                background: 'linear-gradient(135deg, rgba(191, 219, 254, 0.9) 0%, rgba(244, 219, 255, 0.9) 100%)',
                boxShadow: '0 20px 40px rgba(148, 163, 184, 0.25)',
                backdropFilter: 'blur(8px)',
              }}
            >
              <Box sx={{ px: { xs: 2.5, md: 3 }, py: { xs: 2.5, md: 3 } }}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={2}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                >
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                      Grafikler & Tablolar
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'rgba(15,23,42,0.75)' }}>
                      {heroSubtitle}
                    </Typography>
                    {heroChips.length > 0 && (
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                        {heroChips.map((chip) => (
                          <Chip key={chip} label={chip} sx={subtleChipSx} />
                        ))}
                      </Stack>
                    )}
                  </Box>
                </Stack>
              </Box>
            </Box>

            <Paper
              sx={{
                borderRadius: 4,
                ...surfaceBaseSx,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                border: '1px solid rgba(148, 163, 184, 0.16)',
                boxShadow: '0 18px 32px rgba(148, 163, 184, 0.2)',
                backgroundColor: '#ffffff',
              }}
            >
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ xs: 'flex-start', md: 'center' }}
                justifyContent="space-between"
              >
                <Tabs
                  value={activeTab}
                  onChange={(_, v) => setActiveTab(v)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    minHeight: 0,
                    '& .MuiTab-root': {
                      textTransform: 'none',
                      fontWeight: 600,
                      minHeight: 44,
                      borderRadius: 999,
                      mr: 1,
                      px: 1.75,
                      color: '#475569',
                      '&.Mui-selected': {
                        color: '#0f172a',
                        backgroundColor: 'rgba(96, 165, 250, 0.16)',
                      },
                    },
                    '& .MuiTabs-indicator': {
                      height: 0,
                    },
                  }}
                >
                  <Tab label={`Grafikler`} value="graphs" />
                  <Tab label={`Tablolar`} value="tables" />
                  <Tab label={`Analiz`} value="analysis" />
                </Tabs>
                {/* Sağ taraf: VarLabelToggle + Button */}
                {activeTab !== 'analysis' ? (
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    justifyContent="flex-end"
                    sx={{ width: { xs: '100%', md: 'auto' } }}
                  >
                    <VarLabelToggle
                      checked={showVarLabels}
                      onChange={setShowVarLabels}
                    />
                    <Button
                      variant={showSettings ? 'contained' : 'outlined'}
                      startIcon={<SettingsIcon />}
                      onClick={() => setShowSettings((s) => !s)}
                      sx={{
                        ...primaryButtonSx,
                        background: showSettings
                          ? 'linear-gradient(135deg, #bfdbfe 0%, #dbeafe 100%)'
                          : '#ffffff',
                      }}
                    >
                      {showSettings ? 'Ayarları Gizle' : 'Ayarları Aç'}
                    </Button>
                  </Stack>
                ) : (
                  <Box sx={{ flexGrow: 1 }} />
                )}
              </Stack>
              {activeTab === 'graphs' && (
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1.25}
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={() => fetchGraphs()}
                    sx={{ borderRadius: 999 }}
                  >
                    Verileri Güncelle
                  </Button>
                  {needsRefresh && (
                    <Typography variant="body2" sx={{ color: 'warning.main' }}>
                      Veriler güncel değil — <strong>Verilerinizi güncelleyin</strong>
                    </Typography>
                  )}
                </Stack>
              )}
            </Paper>
      {activeTab === 'graphs' && (
        <Collapse in={showSettings} timeout="auto" unmountOnExit>
          <Paper
            sx={{
              borderRadius: 4,
              ...surfaceBaseSx,
              border: '1px solid rgba(148, 163, 184, 0.2)',
              boxShadow: '0 18px 32px rgba(148, 163, 184, 0.18)',
              backgroundColor: '#ffffff',
            }}
          >
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ mb: 2 }}
        >
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel>Varsayılan Grafik Teması</InputLabel>
            <Select
              label="Varsayılan Grafik Teması"
              value={analysisDefaultThemeId || (allColorThemes[0]?.id || DEFAULT_THEME_ID)}
              onChange={(e) => handleChangeAnalysisDefaultTheme(e.target.value)}
              renderValue={(selected) => {
                const theme = getThemeById(selected, allColorThemes);
                return (
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <Typography variant="body2">{theme.label}</Typography>
                    <Stack direction="row" spacing={0.25}>
                      {(theme.palette || []).slice(0, 4).map((color, idx) => (
                        <Box
                          key={`${theme.id}-${idx}`}
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: 0.5,
                            bgcolor: color,
                            border: '1px solid rgba(15, 23, 42, 0.12)',
                          }}
                        />
                      ))}
                    </Stack>
                  </Stack>
                );
              }}
              sx={{ fontSize: '0.85rem' }}
            >
              {allColorThemes.map((theme) => (
                <MenuItem key={theme.id} value={theme.id}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2">{theme.label}</Typography>
                    <Stack direction="row" spacing={0.4}>
                      {theme.palette.slice(0, 5).map((color, idx) => (
                        <Box
                          key={`${theme.id}-${idx}`}
                          sx={{
                            width: 14,
                            height: 10,
                            borderRadius: 0.5,
                            bgcolor: color,
                            border: '1px solid rgba(15, 23, 42, 0.12)',
                          }}
                        />
                      ))}
                    </Stack>
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: { xs: 1.5, sm: 0 } }} flexWrap="wrap" useFlexGap>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={openCreateThemeDialog}
            >
              Yeni Tema
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={openEditThemeDialog}
              disabled={!canEditSelectedTheme}
            >
              Temayı Düzenle
            </Button>
          </Stack>
        </Stack>
        {/* Grup Başlığı ve Kontroller */}
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
          {filterGroups.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Veri Grubu</InputLabel>
              <Select
                label="Veri Grubu"
                value={activeFilterGroup?.id || ''}
                onChange={(e) => handleSelectFilterGroup(e.target.value)}
              >
                {filterGroups.map((grp) => (
                  <MenuItem key={grp.id} value={grp.id}>
                    {grp.name && grp.name.trim() ? grp.name : 'Adsız Grup'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleCreateFilterGroup}
          >
            Grup Oluştur
          </Button>
        </Stack>
        {/* Grup Adı ve Silme butonunu "Grup Oluştur"un altına taşıdık */}
        {filterGroups.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1, mt: 1 }}>
            <TextField
              size="small"
              label="Grup Adı"
              value={activeFilterGroup?.name ?? ''}
              onChange={(e) => updateActiveGroupName(e.target.value)}
              placeholder="Grup adı"
              sx={{ minWidth: 220 }}
            />
            {filterGroups.length >= 1 && (
              <MuiTooltip arrow title="Aktif grubu sil">
                <IconButton size="small" color="error" onClick={handleDeleteActiveFilterGroup}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </MuiTooltip>
            )}
             <Divider sx={{ my: 0.75 }} />
          </Box>
        )}    

        {/* Filtreler Bölümü */}
        {activeFilterGroup && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Filtreler</Typography>
            {filterRows.map((row, idx) => {
            const selectedColMeta = filterCols.find(
              (c) => c.id === row.col || c.header === row.col
            );
            const selectedColId = selectedColMeta ? selectedColMeta.id : (row.col || '');
            const optionItems = selectedColMeta?.options || [];
            const optionLabelMap = selectedColMeta
              ? new Map(optionItems.map((opt) => [opt.value, opt.label]))
              : null;

            return (
              <Stack
                key={idx}
                direction="row"
                spacing={0.75}
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Soru</InputLabel>
                  <Select
                    label="Soru"
                    value={selectedColId}
                    onChange={(e) => handleColChange(idx, e.target.value)}
                  >
                    {filterCols.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {fmt(c.header)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {selectedColId && (
                  <FormControl size="small" sx={{ minWidth: 240 }}>
                    <InputLabel>Cevap</InputLabel>
                    <Select
                      label="Cevap"
                      multiple
                      value={row.vals}
                      onChange={(e) => handleValChange(idx, e.target.value)}
                      renderValue={(selected) => {
                        const values = Array.isArray(selected) ? selected : [];
                        if (!values.length) return '';
                        if (!optionLabelMap) return values.join(', ');
                        return values
                          .map((val) => optionLabelMap.get(val) || val)
                          .join(', ');
                      }}
                    >
                      {optionItems.length ? (
                        optionItems.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            <Checkbox checked={row.vals.indexOf(opt.value) > -1} />
                            <ListItemText primary={opt.label || opt.value} />
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem disabled value="__no-options__">
                          <ListItemText primary="Seçenek bulunamadı" />
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                )}

                <IconButton size="small" onClick={() => removeRow(idx)} sx={{ ml: 0.25 }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            );
          })}

            {activeFilterGroup && (
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={addRow}>
                  Yeni Filtre
                </Button>
              </Stack>
            )}
            
            <Divider sx={{ my: 1.5 }} />
          </Box>
        )}

 

        {/* Ağırlıklandırma Bölümü sadece grafik görünümünde */}
        {activeFilterGroup && activeTab === 'graphs' && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Ağırlıklandırma</Typography>
            {weightRows.map((row,idx)=>(
            <Stack key={idx} direction="row" spacing={0.75} alignItems="center" sx={{mb:0.75}}>
              <FormControl size="small" sx={{ minWidth: 320 }}>
                <InputLabel>Sorular (Multi)</InputLabel>
                <Select
                  multiple
                  value={row.cols}
                  onChange={e=>handleWeightChange(idx,e.target.value)}
                  renderValue={(selected) => {
                    if (!Array.isArray(selected) || !selected.length) return '';
                    if (
                      weightColumnHeaders.length > 0 &&
                      selected.length === weightColumnHeaders.length
                    ) {
                      return 'Hepsini Seç';
                    }
                    return selected
                      .map((header) => fmt(header))
                      .filter(Boolean)
                      .join(', ');
                  }}
                >
                  <MenuItem value={WEIGHT_SELECT_ALL_VALUE}>
                    <Checkbox
                      size="small"
                      checked={
                        weightColumnHeaders.length > 0 &&
                        row.cols.length === weightColumnHeaders.length
                      }
                      indeterminate={
                        row.cols.length > 0 &&
                        row.cols.length < weightColumnHeaders.length
                      }
                    />
                    <ListItemText primary="Hepsini Seç" />
                  </MenuItem>
                  {displayCols.map((c) => {
                    const headerRaw = c?.header ?? '';
                    const header =
                      typeof headerRaw === 'string'
                        ? headerRaw.trim()
                        : String(headerRaw ?? '').trim();
                    if (!header) return null;
                    return (
                      <MenuItem key={header} value={header}>
                        <Checkbox checked={row.cols.includes(header)} size="small"/>
                        <ListItemText primary={fmt(header)}/>
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Ağırlık Kolonu</InputLabel>
                <Select value={row.weightCol}
                  onChange={e=>handleWeightCol(idx,e.target.value)}>
                  {leafColumnOptions.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>
                      <ListItemText primary={fmt(opt.header)} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <IconButton size="small" onClick={()=>removeWeightRow(idx)} sx={{ ml: 0.25 }}>
                <DeleteIcon fontSize="small"/>
              </IconButton>
            </Stack>
          ))}

            <Stack direction="row" spacing={1.25} alignItems="center">
              <Button variant="outlined" size="small" startIcon={<AddIcon/>} onClick={addWeightRow}>
                Ağırlıklandırma Ekle
              </Button>
            </Stack>
          </Box>
        )}
        </Paper>
      </Collapse>
      )}

      {/* ------------ İçerik Alanı: Grafikler, Tablolar ve Analiz -------------- */}
      {activeTab === 'graphs' && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            gap: { xs: 2, md: 2.5 },
          }}
        >
          {/* Sol yan sütun: Gösterilen & Gizlenen listeleri alt alta */}
          <Box
            sx={{
              width: { xs: '100%', lg: 280 },
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              position: { xs: 'static', lg: 'sticky' },
              top: { xs: 'auto', lg: '24px' },
              alignSelf: { xs: 'stretch', lg: 'flex-start' },
              maxHeight: { xs: 'none', lg: 'calc(100vh - 200px)' },
              flexShrink: 0,
            }}
          >
            <Paper
              sx={{
                maxHeight: 560,
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 3,
                border: '1px solid rgba(148, 163, 184, 0.16)',
                boxShadow: '0 16px 28px rgba(148, 163, 184, 0.18)',
                backgroundColor: '#ffffff',
              }}
            >
              <Box sx={{ p: 0.75 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Grafik ara…"
                  value={graphSearch}
                  onChange={(e) => setGraphSearch(e.target.value)}
                  inputProps={{ 'aria-label': 'Grafik ara' }}
                />
              </Box>
              <Box sx={{ px: 0.75, pb: 0.75, display: 'flex', gap: 0.75, justifyContent: 'flex-end' }}>
                <MuiTooltip title="Hepsini göster" arrow>
                  <span>
                    <IconButton
                      size="small"
                      disabled={!hasVisibleGraphs}
                      onClick={() => setSelectedGraphKeys(allVisibleGraphKeys)}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </span>
                </MuiTooltip>
                <MuiTooltip title="Hepsini kapat" arrow>
                  <IconButton size="small" onClick={() => setSelectedGraphKeys([])}>
                    <VisibilityOffIcon fontSize="small" />
                  </IconButton>
                </MuiTooltip>
              </Box>
              <Divider />
              <Box sx={{ overflow: 'auto', flex: 1, p: 0.75 }}>
                {orderedGraphGroups.map(({ id, meta }) => {
                  const totalItems = graphsByGroup[id] || [];
                  const entries = filteredGraphsByGroup[id] || [];
                  const isOpen = openGraphGroupId === id;
                  const isActive = activeGraphGroupId === id;
                  const countLabel = hasGraphSearch ? entries.length : totalItems.length;
                  return (
                    <Box key={id} sx={{ mb: 0.75, borderRadius: 1, border: '1px solid rgba(0,0,0,0.08)' }}>
                      <Button
                        fullWidth
                        size="small"
                        variant={isActive ? 'contained' : 'text'}
                        onClick={() => {
                          setActiveGraphGroupId(id);
                          setOpenGraphGroupId((prev) => (prev === id ? '' : id));
                        }}
                        sx={{ justifyContent: 'space-between', textTransform: 'none', borderRadius: 1 }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>
                            {meta?.name || (id === BASE_GRAPH_GROUP_ID ? 'Temel Grafikler' : id)}
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.7 }}>
                            ({countLabel})
                          </Typography>
                        </Box>
                        <Typography variant="caption" sx={{ opacity: 0.7 }}>
                          {isOpen ? '▲' : '▼'}
                        </Typography>
                      </Button>
                      {isOpen && (
                        <Box sx={{ p: 0.45 }}>
                          {entries.length === 0 ? (
                            <Typography variant="caption" sx={{ opacity: 0.7, pl: 0.75, py: 0.4 }}>
                              {hasGraphSearch ? 'Sonuç bulunamadı.' : 'Grafik bulunamadı.'}
                            </Typography>
                          ) : (
                            <List dense disablePadding>
                              {entries.map(({ key, graph }) => {
                                const isSelected = selectedGraphKeys.includes(key);
                                return (
                                  <ListItemButton
                                    key={key}
                                    dense
                                    selected={isSelected}
                                    onClick={() => toggleSelectedGraph(key)}
                                    sx={{ borderRadius: 1, mb: 0.2, pl: 1.75 }}
                                  >
                                    <ListItemText primary={fmt(graph.header)} />
                                  </ListItemButton>
                                );
                              })}
                            </List>
                          )}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Paper>

            {hiddenHeaders.length > 0 && (
              <Paper
                sx={{
                  maxHeight: 210,
                  overflow: 'auto',
                  borderRadius: 3,
                  border: '1px solid rgba(148, 163, 184, 0.16)',
                  boxShadow: '0 12px 24px rgba(148, 163, 184, 0.15)',
                  backgroundColor: '#ffffff',
                }}
              >
                <List dense>
                  <ListItemButton disabled>
                    <ListItemText primary={`Gizlenen Grafikler (${hiddenHeaders.length})`} />
                  </ListItemButton>
                  {hiddenHeaders.map((h) => (
                    <ListItemButton key={h} onClick={() => showHeaderGraphs(h)}>
                      <ListItemText primary={fmt(h)} />
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            )}
          </Box>

          {/* Seçilen grafikler (scroll'lu kutu) */}
          <Paper
            sx={{
              flex: 1,
              p: 0.75,
              overflow: 'auto',
              display: 'flex',
              borderRadius: 4,
              border: '1px solid rgba(148, 163, 184, 0.16)',
              boxShadow: '0 18px 32px rgba(148, 163, 184, 0.2)',
              backgroundColor: '#ffffff',
            }}
          >
            <Box
              sx={{
                flex: 1,
                display: 'grid',
                gridTemplateColumns: `minmax(520px, 1fr)`,
                gap: 1.6,
              }}
            >
              {displayedGraphs.length === 0 ? (
                <Typography variant="body2" sx={{ mt: 1.5 }}>
                  Görüntülemek için listeden grafik seçin.
                </Typography>
              ) : (
                displayedGraphs.map((g) => (
                  <Box
                    key={graphKeyOf(g)}
                    sx={{
                      border: '1px solid rgba(148, 163, 184, 0.16)',
                      borderRadius: 3,
                      backgroundColor: '#ffffff',
                      boxShadow: '0 14px 24px rgba(148, 163, 184, 0.12)',
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        px: { xs: 1.25, md: 1.75 },
                        py: { xs: 0.85, md: 1.1 },
                        backgroundColor: '#F8FAFC',
                        borderBottom: '1px solid rgba(148, 163, 184, 0.12)',
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {fmt(g.header)}
                      </Typography>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <MuiTooltip title="Grafiği Sil" placement="left" arrow>
                          <IconButton
                            size="small"
                            onClick={() => setDeleteDlg({ open: true, header: g.header, busy: false })}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </MuiTooltip>
                        <MuiTooltip title="Bu grafiği bu analiz listesinde gizle" placement="left" arrow>
                          <IconButton
                            size="small"
                            onClick={() => hideGraph(g)}
                          >
                            <VisibilityOffIcon fontSize="small" />
                          </IconButton>
                        </MuiTooltip>
                      </Stack>
                    </Box>

                    <ChartCard
                      graph={normalizeGraphForRender(g)}
                      onAddToPresentation={(graph) =>
                        setAddDlg({ open: true, graph: prepareGraphForPresentation(graph), presId: '' })
                      }
                      onSaveSettings={(header, settings, applyKeys) =>
                        handleSaveSettingsForGraph(g, header, settings, applyKeys)
                      }
                      showVarLabels={showVarLabels}
                      setShowVarLabels={setShowVarLabels}
                      formatVarLabel={fmt}
                      colorThemes={allColorThemes}
                      defaultColorThemeId={analysisDefaultThemeId}
                    />
                  </Box>
                ))
              )}
            </Box>
          </Paper>
        </Box>
      )}

      {activeTab === 'tables' && (
        <Paper
          sx={{
            borderRadius: 4,
            ...surfaceBaseSx,
            border: '1px solid rgba(148, 163, 184, 0.16)',
            boxShadow: '0 18px 32px rgba(148, 163, 184, 0.2)',
            backgroundColor: '#ffffff',
          }}
        >
          <TableCardPanel
            tableCards={tableCards}
            onChange={(next) => setTableCards(next)}
            db={db}
            persistCtx={{ organisation, projectID, analysisID }}
            autoLoad={true}
            autoPersistOnImport={true}
            projectID={projectID}
            currentUserEmail={currentUser?.email}
            colTypes={colTypes}
            showVarLabels={showVarLabels}
            onToggleVarLabels={setShowVarLabels}
            filters={filterRows}
            filterGroupPayload={filterGroupPayload}
            toId={(key) => idByHeader[key] || key}
            toIds={(arr) => (Array.isArray(arr) ? arr.map((k) => idByHeader[k] || k) : [])}
            colorThemes={allColorThemes}
            defaultColorThemeId={analysisDefaultThemeId}
            onNormalizedQueryChange={handleBuilderInputsChange}
          />
        </Paper>
      )}

      {activeTab === 'analysis' && (
        <Paper
          sx={{
            borderRadius: 4,
            ...surfaceBaseSx,
            border: '1px solid rgba(148, 163, 184, 0.16)',
            boxShadow: '0 18px 32px rgba(148, 163, 184, 0.2)',
            backgroundColor: '#ffffff',
          }}
        >
          <AnalysisCard
            db={db}
            organisation={organisation}
            projectID={projectID}
            analysisID={analysisID}
            currentUser={currentUser}
            builderInputs={tableBuilderInputs?.inputs || null}
            pivotOptions={pivotOpts}
          />
        </Paper>
      )}
          </Stack>

      <Dialog
        open={themeEditor.open}
        onClose={() => {
          if (themeEditor.saving || themeEditor.deleteBusy) return;
          closeThemeEditor();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {themeEditor.mode === 'edit' ? 'Temayı Düzenle' : 'Yeni Tema Oluştur'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {themeEditor.mode === 'edit' && (
              <FormControl size="small">
                <InputLabel>Temalar</InputLabel>
                <Select
                  label="Temalar"
                  value={themeEditor.themeId}
                  onChange={(e) => {
                    const nextId = e.target.value;
                    const nextTheme = customColorThemes.find((theme) => theme.id === nextId);
                    if (!nextTheme) return;
                    setThemeEditor((prev) => ({
                      ...prev,
                      themeId: nextTheme.id,
                      label: nextTheme.label,
                      palette: Array.isArray(nextTheme.palette) && nextTheme.palette.length
                        ? [...nextTheme.palette]
                        : getInitialThemePalette(),
                      error: '',
                    }));
                  }}
                  disabled={themeEditor.saving || themeEditor.deleteBusy}
                >
                  {customColorThemes.map((theme) => (
                    <MenuItem key={theme.id} value={theme.id}>
                      {theme.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <TextField
              autoFocus={themeEditor.mode !== 'edit'}
              label="Tema Adı"
              value={themeEditor.label}
              onChange={(e) => handleThemeLabelChange(e.target.value)}
              disabled={themeEditor.saving || themeEditor.deleteBusy}
              placeholder="Örn. Kurumsal Mavi"
            />
            <Stack spacing={1.25}>
              {themeEditor.palette.map((color, idx) => {
                const sanitized = sanitizeHexColor(color) || '#000000';
                return (
                  <Stack
                    key={`palette-${idx}`}
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ flexWrap: 'wrap' }}
                  >
                    <Box
                      component="input"
                      type="color"
                      value={sanitized}
                      onChange={(e) => handleThemePaletteChange(idx, e.target.value)}
                      disabled={themeEditor.saving || themeEditor.deleteBusy}
                      sx={{
                        width: 48,
                        height: 36,
                        p: 0,
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                      }}
                    />
                    <TextField
                      label={`Renk ${idx + 1}`}
                      value={color || ''}
                      onChange={(e) => handleThemePaletteChange(idx, e.target.value)}
                      disabled={themeEditor.saving || themeEditor.deleteBusy}
                      placeholder="#8639A1"
                      sx={{ flex: 1, minWidth: 160 }}
                      inputProps={{ maxLength: 9 }}
                    />
                    {themeEditor.palette.length > 2 && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleThemePaletteRemove(idx)}
                        disabled={themeEditor.saving || themeEditor.deleteBusy}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                );
              })}
              {themeEditor.palette.length < 10 && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleThemePaletteAdd}
                  disabled={themeEditor.saving || themeEditor.deleteBusy}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Renk Ekle
                </Button>
              )}
            </Stack>
            {themeEditor.error && (
              <Typography variant="body2" color="error">
                {themeEditor.error}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closeThemeEditor}
            disabled={themeEditor.saving || themeEditor.deleteBusy}
          >
            Vazgeç
          </Button>
          {themeEditor.mode === 'edit' && (
            <Button
              color="error"
              onClick={handleThemeDelete}
              disabled={themeEditor.saving || themeEditor.deleteBusy}
            >
              {themeEditor.deleteBusy ? 'Siliniyor…' : 'Temayı Sil'}
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleThemeSave}
            disabled={themeEditor.saving || themeEditor.deleteBusy}
          >
            {themeEditor.saving ? 'Kaydediliyor…' : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------- Silme Onayı Diyaloğu ---------------- */}
      <Dialog open={deleteDlg.open} onClose={() => !deleteDlg.busy && setDeleteDlg({ open:false, header:'', busy:false })}>
        <DialogTitle>Grafiği silmek istiyor musunuz?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            <strong>{deleteDlg.header}</strong> başlığına ait grafik(ler) kalıcı olarak kaldırılacak. Bu işlem geri alınamaz.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDlg({ open:false, header:'', busy:false })} disabled={deleteDlg.busy}>Vazgeç</Button>
          <Button
            variant="contained"
            color="error"
            disabled={deleteDlg.busy}
            onClick={async () => {
              try {
                setDeleteDlg((s) => ({ ...s, busy: true }));
                await handleDeleteChart(deleteDlg.header);
              } finally {
                setDeleteDlg({ open:false, header:'', busy:false });
              }
            }}
          >
            Sil
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------- “Rapora Ekle” Diyaloğu ---------------- */}
      <Dialog open={addDlg.open} onClose={()=>setAddDlg({open:false})}>
        <DialogTitle>Grafiği hangi sunuma ekleyelim?</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{mt:1}}>
            <InputLabel>Sunum</InputLabel>
            <Select
              label="Sunum"
              value={addDlg.presId}
              onChange={e=>setAddDlg(s=>({...s, presId:e.target.value}))}
            >
              {presentations.map(p=>(
                <MenuItem key={p.id} value={p.id}>{p.title||p.id}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setAddDlg({open:false})}>Vazgeç</Button>
          <Button
            variant="contained"
            disabled={!addDlg.presId}
            onClick={async ()=>{
              try{
                const prepared = prepareGraphForPresentation(addDlg.graph);
                if (!prepared) throw new Error('Graph verisi bulunamadı');
                await addDoc(
                  collection(
                    db,'organisations',organisation,
                    'presentations',addDlg.presId,'savedGraphs'
                  ),
                  { graph: prepared, addedAt:serverTimestamp() }
                );
              }catch(err){ console.error('Kaydetme hatası',err); }
              setAddDlg({open:false});
            }}
          >
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>

        </Container>
      </Box>
    </ThemeProvider>
  );
}
